import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  BarChart3, Plus, TrendingUp, TrendingDown, DollarSign, Receipt, Wallet, FileDown, CheckCircle,
  Users, Calendar, Clock, AlertTriangle, Phone, Mail, FileText, Scale, Pause, ChevronDown, ChevronRight,
  Save, Trash2, Eye, Edit, Building, ArrowLeftRight, PiggyBank, Package, UserCog, CreditCard, 
  PhoneCall, RotateCcw, Construction, Upload, Landmark, RefreshCw, Search, History, ArrowUpCircle, 
  ArrowDownCircle, FileSpreadsheet, X, Settings, Lightbulb, Copy, Zap, Info, Link, ExternalLink, User, XCircle,
  AlertOctagon, UserX
} from 'lucide-react';
import api, { formatErrorMessage, settleRequests } from '../utils/api';
import { toast } from 'sonner';

// Componentes refactorizados
import { DebouncedInput, DebouncedTextarea } from './finanzas/components/shared/DebouncedInputs';
import { RegistrarPagoModal, NuevaGestionModal, NotaCreditoModal, NotaDebitoModal } from './finanzas/components/modals';
import CajaChica from '../components/CajaChica';
import GastosRemuneraciones from '../components/GastosRemuneraciones';
import PlaceholderTab from './finanzas/components/tabs/PlaceholderTab';
import RRHHTab from './finanzas/components/tabs/RRHHTab';
import FlujoCajaTab from './finanzas/components/tabs/FlujoCajaTab';
import CajasFondoTab from './finanzas/components/tabs/CajasFondoTab';
import IngresosTab from './finanzas/components/tabs/IngresosTab';
import FacturacionTab from './finanzas/components/tabs/FacturacionTab';
import CobranzaTab from './finanzas/components/tabs/CobranzaTab';
import ConciliacionTab from './finanzas/components/tabs/ConciliacionTab';

const Finanzas = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [propuestasAceptadas, setPropuestasAceptadas] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [cajasFondo, setCajasFondo] = useState([]);
  const [presupuesto, setPresupuesto] = useState(null);
  const [selectedMes, setSelectedMes] = useState(new Date().toISOString().slice(0, 7));
  const [showNewGasto, setShowNewGasto] = useState(false);
  const [showNewCaja, setShowNewCaja] = useState(false);
  
  // Get active tab from URL query parameter
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get('tab') || 'ingresos';

  // Permisos de Finanzas (acceso restringido por tab para usuarios sin rol Líder/Admin)
  const finanzasAccess = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const perms = u.permisos_finanzas || [];
      const role = u.role || '';
      const areas = u.areas || [];
      const esGestorTotal =
        role === 'Administrador' ||
        (role === 'Líder' && (areas.includes('Adm. y Finanzas') || areas.includes('Socios')));
      return { esGestorTotal, permisos: perms };
    } catch {
      return { esGestorTotal: true, permisos: [] };
    }
  }, []);

  const tabPermitido = (tabId) => {
    if (finanzasAccess.esGestorTotal) return true;
    if (finanzasAccess.permisos.length === 0) return true;
    return finanzasAccess.permisos.includes(tabId);
  };

  // Si la tab solicitada no está permitida para este usuario, redirigir a la primera permitida
  const activeTab = useMemo(() => {
    if (tabPermitido(requestedTab)) return requestedTab;
    if (finanzasAccess.permisos.length > 0) return finanzasAccess.permisos[0];
    return 'ingresos';
  }, [requestedTab, finanzasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== requestedTab) {
      navigate(`/finanzas?tab=${activeTab}`, { replace: true });
    }
  }, [activeTab, requestedTab, navigate]);

  // Handler to change tab and update URL
  const handleTabChange = (newTab) => {
    navigate(`/finanzas?tab=${newTab}`, { replace: true });
  };
  
  // Estados para Minutas de Cobranzas
  const [minutasCobranza, setMinutasCobranza] = useState([]);
  const [clientesMorosos, setClientesMorosos] = useState([]);
  const [showNewMinuta, setShowNewMinuta] = useState(false);
  const [showViewMinuta, setShowViewMinuta] = useState(false);
  const [selectedMinuta, setSelectedMinuta] = useState(null);
  const [proximaReunion, setProximaReunion] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingMinutas, setLoadingMinutas] = useState(false);
  
  const [newMinuta, setNewMinuta] = useState({
    fecha_reunion: new Date().toISOString().slice(0, 10),
    hora_reunion: '10:00',
    asistentes: [],
    asistentes_nombres: [],
    items: [],
    observaciones_generales: '',
    proxima_reunion: ''
  });

  // Función para formatear RUT sin puntos (solo guión antes del dígito verificador)
  const formatRutSinPuntos = (rut) => {
    if (!rut) return 'N/A';
    // Eliminar todos los puntos y espacios
    return rut.replace(/\./g, '').trim();
  };
  
  const decisionesCobranza = [
    { value: 'Llamar', label: 'Llamar', icon: Phone },
    { value: 'Enviar Correo', label: 'Enviar Correo', icon: Mail },
    { value: 'Enviar Carta', label: 'Enviar Carta', icon: FileText },
    { value: 'Convenio de Pago', label: 'Convenio de Pago', icon: FileText },
    { value: 'Suspender Servicio', label: 'Suspender Servicio', icon: Pause },
    { value: 'Derivar a Legal', label: 'Derivar a Legal', icon: Scale },
    { value: 'Esperar', label: 'Esperar', icon: Clock },
    { value: 'Otra', label: 'Otra', icon: FileText }
  ];
  
  const [newGasto, setNewGasto] = useState({
    tipo: 'Fijo',
    categoria: '',
    descripcion: '',
    monto: '',
    fecha: new Date().toISOString().slice(0, 10)
  });
  
  const [newCaja, setNewCaja] = useState({
    responsable: '',
    monto: '',
    descripcion: '',
    fecha_entrega: new Date().toISOString().slice(0, 10)
  });
  
  // Estados para Módulo de Ingresos Proyectados
  const [ingresosData, setIngresosData] = useState({ propuestas: [], proyeccion_mensual: [], totales: {} });
  const [loadingIngresos, setLoadingIngresos] = useState(false);
  const [filtroArea, setFiltroArea] = useState('todas');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [areasDisponibles, setAreasDisponibles] = useState([]);
  const [expandedPSP, setExpandedPSP] = useState(null);
  
  // Estados para Proyección de Facturación Futura
  const [proyeccionData, setProyeccionData] = useState({ cuotas: [], meses_proyectados: [], totales: {} });
  const [loadingProyeccion, setLoadingProyeccion] = useState(false);
  const [busquedaProyeccion, setBusquedaProyeccion] = useState('');
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  
  // Estados para Modal de Facturación
  const [showFacturarModal, setShowFacturarModal] = useState(false);
  const [cuotaAFacturar, setCuotaAFacturar] = useState(null);
  const [datosFactura, setDatosFactura] = useState({
    numero_factura: '',
    fecha_emision: new Date().toISOString().slice(0, 10),
    fecha_vencimiento: '',
    monto_facturado: '',
    servicio: '',
    descripcion: ''
  });
  const [facturando, setFacturando] = useState(false);
  const [valorUF, setValorUF] = useState(null);
  const [loadingUF, setLoadingUF] = useState(false);
  
  // Estados para Módulo de Cobranzas
  const [cobranzasData, setCobranzasData] = useState({ facturas: [], en_cobro: [], pagadas: [], totales: {} });
  const [loadingCobranzas, setLoadingCobranzas] = useState(false);
  const [busquedaCobranzas, setBusquedaCobranzas] = useState('');
  const [showFacturasPagadas, setShowFacturasPagadas] = useState(false);
  
  // Estados para Modal de Registro de Pago (Opera en CLP)
  const [showRegistrarPago, setShowRegistrarPago] = useState(false);
  const [facturaSeleccionadaPago, setFacturaSeleccionadaPago] = useState(null);
  const [historialPagos, setHistorialPagos] = useState([]);
  const [loadingPagos, setLoadingPagos] = useState(false);
  
  // Estados para Modal de Historial Financiero Completo
  const [showHistorialFinanciero, setShowHistorialFinanciero] = useState(false);
  const [historialFinancieroData, setHistorialFinancieroData] = useState(null);
  const [loadingHistorialFinanciero, setLoadingHistorialFinanciero] = useState(false);
  
  const [nuevoPago, setNuevoPago] = useState({
    amount_clp: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'transferencia',
    reference: '',
    notes: ''
  });
  
  const metodosPago = [
    { value: 'transferencia', label: 'Transferencia Bancaria' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta', label: 'Tarjeta de Crédito/Débito' },
    { value: 'flow', label: 'Flow' },
    { value: 'duemint', label: 'Duemint' },
    { value: 'otro', label: 'Otro' }
  ];

  // Estados para Modal de Nota de Crédito
  const [showNotaCredito, setShowNotaCredito] = useState(false);
  const [facturaNotaCredito, setFacturaNotaCredito] = useState(null);
  const [notaCreditoForm, setNotaCreditoForm] = useState({
    document_number: '',
    issue_date: new Date().toISOString().slice(0, 10),
    amount_clp: '',
    reason: '',
    reference_text: '',
    notes: ''
  });

  // Estados para Modal de Nota de Débito
  const [showNotaDebito, setShowNotaDebito] = useState(false);
  const [facturaNotaDebito, setFacturaNotaDebito] = useState(null);
  const [notaDebitoForm, setNotaDebitoForm] = useState({
    document_number: '',
    issue_date: new Date().toISOString().slice(0, 10),
    amount_clp: '',
    reason: '',
    reference_text: '',
    notes: ''
  });

  // Estados para historial de documentos de ajuste
  const [documentosAjuste, setDocumentosAjuste] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  
  // Estados para Conciliación de Facturas (Etapa 4)
  const [showConciliarFactura, setShowConciliarFactura] = useState(false);
  const [movimientoAConciliar, setMovimientoAConciliar] = useState(null);
  const [sugerenciasConciliacion, setSugerenciasConciliacion] = useState([]);
  const [loadingSugerencias, setLoadingSugerencias] = useState(false);
  const [ufValueConciliacion, setUfValueConciliacion] = useState(38500);
  const [montoConciliar, setMontoConciliar] = useState('');
  const [facturaSeleccionadaConciliar, setFacturaSeleccionadaConciliar] = useState(null);

  // Estados para Módulo de Conciliación Bancaria
  const [conciliacionData, setConciliacionData] = useState({
    cuentas_activas: 0,
    movimientos_mes: 0,
    total_abonos: 0,
    total_cargos: 0,
    balance_mes: 0,
    pendientes_revision: 0,
    ultimas_cargas: []
  });
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [movimientosBancarios, setMovimientosBancarios] = useState([]);
  const [historialCargas, setHistorialCargas] = useState([]);
  const [loadingConciliacion, setLoadingConciliacion] = useState(false);
  const [busquedaMovimientos, setBusquedaMovimientos] = useState('');
  const [filtroCuenta, setFiltroCuenta] = useState('todas');
  const [filtroTipoMovimiento, setFiltroTipoMovimiento] = useState('todos');
  const [filtroEstadoRevision, setFiltroEstadoRevision] = useState('todos');
  
  // Modales de Conciliación
  const [showNuevaCuenta, setShowNuevaCuenta] = useState(false);
  const [showCargarCartola, setShowCargarCartola] = useState(false);
  const [showHistorialCargas, setShowHistorialCargas] = useState(false);
  const [showDetalleMovimiento, setShowDetalleMovimiento] = useState(false);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);
  const [cargaSeleccionada, setCargaSeleccionada] = useState(null);
  const [showDetalleCarga, setShowDetalleCarga] = useState(false);
  
  // Estados para Módulo de Estados de Cuenta
  const [estadoCuentaData, setEstadoCuentaData] = useState(null);
  const [loadingEstadoCuenta, setLoadingEstadoCuenta] = useState(false);
  const [clientesEstadoCuenta, setClientesEstadoCuenta] = useState([]);
  const [filtrosEstadoCuenta, setFiltrosEstadoCuenta] = useState({
    client_id: '',
    fecha_desde: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    fecha_hasta: new Date().toISOString().slice(0, 10),
    estado_filtro: 'todos'
  });
  
  // Formularios de Conciliación
  const [nuevaCuenta, setNuevaCuenta] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    currency: 'CLP',
    is_active: true
  });
  const [archivoCartola, setArchivoCartola] = useState(null);
  const [cuentaSeleccionadaCarga, setCuentaSeleccionadaCarga] = useState('');
  const [periodoReferencia, setPeriodoReferencia] = useState('');
  const [cargandoCartola, setCargandoCartola] = useState(false);
  
  // Estados para Clasificación de Movimientos (Etapa 2)
  const [categorias, setCategorias] = useState([]);
  const [categoriasFlat, setCategoriasFlat] = useState([]);
  const [filtroClasificacion, setFiltroClasificacion] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroTipoCategoria, setFiltroTipoCategoria] = useState('todos');
  const [showClasificarModal, setShowClasificarModal] = useState(false);
  const [movimientoAClasificar, setMovimientoAClasificar] = useState(null);
  const [clasificacionForm, setClasificacionForm] = useState({
    category_id: '',
    counterparty_name: '',
    notes: ''
  });
  const [clasificando, setClasificando] = useState(false);
  const [resumenClasificacion, setResumenClasificacion] = useState({
    total_movimientos: 0,
    sin_clasificar: 0,
    con_sugerencia: 0,
    clasificados: 0,
    porcentaje_clasificado: 0
  });

  // Estados para Reglas de Clasificación (Etapa 3)
  const [reglas, setReglas] = useState([]);
  const [showReglasPanel, setShowReglasPanel] = useState(false);
  const [showCrearRegla, setShowCrearRegla] = useState(false);
  const [reglaForm, setReglaForm] = useState({
    text_pattern: '',
    match_type: 'contains',
    movement_type: '',
    category_id: '',
    counterparty_name: '',
    amount_min: '',
    amount_max: '',
    bank_account_id: '',
    auto_apply: true,
    priority: 50,
    is_active: true
  });
  const [creandoRegla, setCreandoRegla] = useState(false);
  const [filtroReglasActivas, setFiltroReglasActivas] = useState('todas');
  const [resumenReglas, setResumenReglas] = useState({ total_reglas: 0, activas: 0, auto_aplicar: 0 });

  // Estados para Gestiones de Cobranza (Etapa 6)
  const [gestionesCobranza, setGestionesCobranza] = useState([]);
  const [compromisosCobranza, setCompromisosCobranza] = useState([]);
  const [loadingGestiones, setLoadingGestiones] = useState(false);
  const [showNuevaGestion, setShowNuevaGestion] = useState(false);
  const [showNuevoCompromiso, setShowNuevoCompromiso] = useState(false);
  const [showHistorialGestiones, setShowHistorialGestiones] = useState(false);
  const [clienteGestionSeleccionado, setClienteGestionSeleccionado] = useState(null);
  const [historialGestionesData, setHistorialGestionesData] = useState(null);
  const [filtroGestionCliente, setFiltroGestionCliente] = useState('');
  const [filtroGestionTipo, setFiltroGestionTipo] = useState('todos');
  const [filtroGestionResultado, setFiltroGestionResultado] = useState('todos');
  
  const [nuevaGestion, setNuevaGestion] = useState({
    cliente_id: '',
    cliente_nombre: '',
    tipo_gestion: 'llamada',
    canal: 'telefono',
    resumen: '',
    resultado: '',
    proxima_accion: '',
    fecha_proxima_gestion: ''
  });
  
  const [nuevoCompromiso, setNuevoCompromiso] = useState({
    cliente_id: '',
    cliente_nombre: '',
    monto_comprometido: '',
    fecha_compromiso: '',
    observaciones: ''
  });
  
  // Catálogos de tipos para gestiones de cobranza
  const tiposGestion = [
    { value: 'llamada', label: 'Llamada telefónica', icon: PhoneCall },
    { value: 'correo_enviado', label: 'Correo enviado', icon: Mail },
    { value: 'correo_recibido', label: 'Correo recibido', icon: Mail },
    { value: 'reunion', label: 'Reunión', icon: Users },
    { value: 'whatsapp', label: 'WhatsApp', icon: Phone },
    { value: 'recordatorio', label: 'Recordatorio', icon: Clock },
    { value: 'compromiso_pago', label: 'Compromiso de pago', icon: CreditCard },
    { value: 'seguimiento_interno', label: 'Seguimiento interno', icon: FileText },
    { value: 'derivacion', label: 'Derivación', icon: ArrowLeftRight },
    { value: 'otra', label: 'Otra gestión', icon: FileText }
  ];
  
  const canalesGestion = [
    { value: 'telefono', label: 'Teléfono' },
    { value: 'email', label: 'Email' },
    { value: 'presencial', label: 'Presencial' },
    { value: 'videollamada', label: 'Videollamada' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'interno', label: 'Interno' }
  ];
  
  const resultadosGestion = [
    { value: 'sin_respuesta', label: 'Sin respuesta', color: 'slate' },
    { value: 'cliente_informado', label: 'Cliente informado', color: 'blue' },
    { value: 'pago_prometido', label: 'Pago prometido', color: 'green' },
    { value: 'pago_parcial_prometido', label: 'Pago parcial prometido', color: 'lime' },
    { value: 'cliente_objeta_saldo', label: 'Cliente objeta saldo', color: 'amber' },
    { value: 'cliente_solicita_plazo', label: 'Cliente solicita plazo', color: 'orange' },
    { value: 'pago_recibido', label: 'Pago recibido', color: 'emerald' },
    { value: 'derivar_revision', label: 'Derivar a revisión', color: 'purple' },
    { value: 'derivar_judicial', label: 'Derivar a judicial', color: 'red' },
    { value: 'cerrado', label: 'Cerrado', color: 'gray' }
  ];

  // Estados para Alertas de Cobranza (Etapa 7)
  const [alertasCobranza, setAlertasCobranza] = useState(null);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [generandoAlertas, setGenerandoAlertas] = useState(false);
  const [showCrearGestionDesdeAlerta, setShowCrearGestionDesdeAlerta] = useState(false);
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
  const [historialAlertas, setHistorialAlertas] = useState([]);
  const [showHistorialAlertas, setShowHistorialAlertas] = useState(false);

  // Mapeo de iconos para tipos de alerta
  const iconosAlerta = {
    factura_proxima_vencer: Clock,
    factura_vencida: AlertTriangle,
    factura_vencida_7: AlertTriangle,
    factura_vencida_15: AlertTriangle,
    factura_vencida_30: AlertOctagon,
    factura_vencida_60: AlertOctagon,
    factura_vencida_90: XCircle,
    compromiso_proximo: Calendar,
    compromiso_hoy: Calendar,
    compromiso_incumplido: XCircle,
    cliente_critico: UserX
  };

  // Mapeo de colores para prioridades
  const coloresPrioridad = {
    baja: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
    media: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    alta: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
    critica: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' }
  };

  // Mapeo de sugerencias de acción
  const sugerenciasAccion = {
    recordatorio_cordial: { label: 'Recordatorio cordial', descripcion: 'Enviar un recordatorio amable sobre el próximo vencimiento' },
    contacto_telefonico: { label: 'Contacto telefónico', descripcion: 'Realizar llamada de seguimiento al cliente' },
    seguimiento_formal: { label: 'Seguimiento formal', descripcion: 'Enviar comunicación formal por escrito' },
    revision_interna: { label: 'Revisión interna', descripcion: 'Escalar para revisión del equipo de finanzas' },
    derivacion_judicial: { label: 'Derivación judicial', descripcion: 'Considerar iniciar proceso de cobro judicial' }
  };

  // Estados para Comunicaciones de Cobranza (Etapa 8)
  const [comunicacionesPanel, setComunicacionesPanel] = useState(null);
  const [loadingComunicaciones, setLoadingComunicaciones] = useState(false);
  const [historialComunicaciones, setHistorialComunicaciones] = useState([]);
  const [plantillasCorreo, setPlantillasCorreo] = useState([]);
  const [showEnviarEstadoCuenta, setShowEnviarEstadoCuenta] = useState(false);
  const [showEnviarRecordatorio, setShowEnviarRecordatorio] = useState(false);
  const [clienteEnvioSeleccionado, setClienteEnvioSeleccionado] = useState(null);
  const [facturaEnvioSeleccionada, setFacturaEnvioSeleccionada] = useState(null);
  const [enviandoComunicacion, setEnviandoComunicacion] = useState(false);
  const [showConfigComunicaciones, setShowConfigComunicaciones] = useState(false);
  const [configComunicaciones, setConfigComunicaciones] = useState(null);
  const [ejecutandoAutomatico, setEjecutandoAutomatico] = useState(false);

  // Tipos de comunicación para UI
  const tiposComunicacion = {
    estado_cuenta_mensual: { label: 'Estado de Cuenta', color: 'blue', icon: FileText },
    recordatorio_preventivo: { label: 'Recordatorio Preventivo', color: 'amber', icon: Clock },
    recordatorio_vencida: { label: 'Recordatorio Vencida', color: 'red', icon: AlertTriangle },
    recordatorio_compromiso: { label: 'Recordatorio Compromiso', color: 'green', icon: Calendar },
    aviso_incumplimiento: { label: 'Aviso Incumplimiento', color: 'red', icon: XCircle },
    notificacion_manual: { label: 'Notificación Manual', color: 'slate', icon: Mail }
  };

  const estadosComunicacion = {
    pendiente: { label: 'Pendiente', color: 'amber' },
    enviado: { label: 'Enviado', color: 'green' },
    fallido: { label: 'Fallido', color: 'red' },
    simulado: { label: 'Simulado', color: 'blue' }
  };

  useEffect(() => {
    fetchData();
    fetchMinutasData();
    fetchIngresosData();
    fetchProyeccionData();
    fetchCobranzasData();
    fetchValorUF();
  }, [selectedMes]);

  // Fetch datos de conciliación cuando se cambia al tab
  useEffect(() => {
    if (activeTab === 'conciliacion') {
      fetchConciliacionData();
    }
  }, [activeTab]);
  
  // Fetch clientes cuando se abre el tab de Cobranza (incluye Estados de Cuenta y Gestiones)
  useEffect(() => {
    if (activeTab === 'cobranza') {
      fetchClientesEstadoCuenta();
      fetchGestionesCobranza();
      fetchCompromisosCobranza();
      fetchAlertasCobranza();
      fetchComunicacionesPanel();
    }
  }, [activeTab]);
  
  // ==================== FUNCIONES COMUNICACIONES DE COBRANZA (ETAPA 8) ====================

  // Fetch panel de comunicaciones
  const fetchComunicacionesPanel = async () => {
    setLoadingComunicaciones(true);
    try {
      const response = await api.get('/finanzas/cobranza/comunicaciones/panel');
      setComunicacionesPanel(response.data);
    } catch (error) {
      console.error('Error al cargar panel de comunicaciones:', error);
      setComunicacionesPanel(null);
    } finally {
      setLoadingComunicaciones(false);
    }
  };

  // Fetch historial de comunicaciones
  const fetchHistorialComunicaciones = async () => {
    try {
      const response = await api.get('/finanzas/cobranza/comunicaciones', { params: { limit: 100 } });
      setHistorialComunicaciones(response.data.comunicaciones || []);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setHistorialComunicaciones([]);
    }
  };

  // Fetch plantillas de correo
  const fetchPlantillasCorreo = async () => {
    try {
      const response = await api.get('/finanzas/cobranza/comunicaciones/plantillas');
      setPlantillasCorreo(response.data.plantillas || []);
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
      setPlantillasCorreo([]);
    }
  };

  // Fetch configuración de comunicaciones
  const fetchConfigComunicaciones = async () => {
    try {
      const response = await api.get('/finanzas/cobranza/comunicaciones/configuracion');
      setConfigComunicaciones(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  // Enviar estado de cuenta por email
  const handleEnviarEstadoCuenta = async (clienteId, mensajeAdicional = null) => {
    setEnviandoComunicacion(true);
    try {
      const params = { cliente_id: clienteId };
      if (mensajeAdicional) params.mensaje_adicional = mensajeAdicional;
      
      const response = await api.post('/finanzas/cobranza/comunicaciones/enviar-estado-cuenta', null, { params });
      
      if (response.data.success) {
        if (response.data.simulado) {
          toast.info('Estado de cuenta registrado (modo simulado - Resend no configurado)');
        } else {
          toast.success('Estado de cuenta enviado exitosamente');
        }
        setShowEnviarEstadoCuenta(false);
        fetchComunicacionesPanel();
      } else {
        toast.error(response.data.message || 'Error al enviar estado de cuenta');
      }
    } catch (error) {
      console.error('Error al enviar estado de cuenta:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar estado de cuenta');
    } finally {
      setEnviandoComunicacion(false);
    }
  };

  // Enviar recordatorio de factura
  const handleEnviarRecordatorio = async (facturaId, tipo = 'recordatorio_vencida', mensajeAdicional = null) => {
    setEnviandoComunicacion(true);
    try {
      const params = { factura_id: facturaId, tipo };
      if (mensajeAdicional) params.mensaje_adicional = mensajeAdicional;
      
      const response = await api.post('/finanzas/cobranza/comunicaciones/enviar-recordatorio', null, { params });
      
      if (response.data.success) {
        if (response.data.simulado) {
          toast.info('Recordatorio registrado (modo simulado)');
        } else {
          toast.success('Recordatorio enviado exitosamente');
        }
        setShowEnviarRecordatorio(false);
        fetchComunicacionesPanel();
      } else {
        toast.error(response.data.message || 'Error al enviar recordatorio');
      }
    } catch (error) {
      console.error('Error al enviar recordatorio:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
    } finally {
      setEnviandoComunicacion(false);
    }
  };

  // Ejecutar comunicaciones automáticas
  const handleEjecutarAutomatico = async (tipo = 'todos') => {
    setEjecutandoAutomatico(true);
    try {
      const response = await api.post('/finanzas/cobranza/comunicaciones/ejecutar-automatico', null, { params: { tipo } });
      
      if (response.data.success) {
        const r = response.data.resultados;
        toast.success(`Ejecución completada: ${r.recordatorios_vencidas_enviados} recordatorios enviados`);
        fetchComunicacionesPanel();
      }
    } catch (error) {
      console.error('Error al ejecutar automático:', error);
      toast.error('Error al ejecutar comunicaciones automáticas');
    } finally {
      setEjecutandoAutomatico(false);
    }
  };
  
  // Fetch gestiones de cobranza
  const fetchGestionesCobranza = async () => {
    setLoadingGestiones(true);
    try {
      const params = {};
      if (filtroGestionCliente) params.cliente_id = filtroGestionCliente;
      if (filtroGestionTipo && filtroGestionTipo !== 'todos') params.tipo_gestion = filtroGestionTipo;
      if (filtroGestionResultado && filtroGestionResultado !== 'todos') params.resultado = filtroGestionResultado;
      
      const response = await api.get('/finanzas/cobranza/gestiones', { params });
      setGestionesCobranza(response.data.gestiones || []);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      setGestionesCobranza([]);
    } finally {
      setLoadingGestiones(false);
    }
  };
  
  // Fetch compromisos de pago
  const fetchCompromisosCobranza = async () => {
    try {
      const response = await api.get('/finanzas/cobranza/compromisos');
      setCompromisosCobranza(response.data.compromisos || []);
    } catch (error) {
      console.error('Error al cargar compromisos:', error);
      setCompromisosCobranza([]);
    }
  };
  
  // Crear nueva gestión de cobranza
  const handleCrearGestion = async () => {
    if (!nuevaGestion.cliente_id || !nuevaGestion.resumen) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    try {
      const response = await api.post('/finanzas/cobranza/gestiones', nuevaGestion);
      toast.success('Gestión registrada exitosamente');
      setShowNuevaGestion(false);
      setNuevaGestion({
        cliente_id: '',
        cliente_nombre: '',
        tipo_gestion: 'llamada',
        canal: 'telefono',
        resumen: '',
        resultado: '',
        proxima_accion: '',
        fecha_proxima_gestion: ''
      });
      fetchGestionesCobranza();
    } catch (error) {
      console.error('Error al crear gestión:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar gestión');
    }
  };
  
  // Crear nuevo compromiso de pago
  const handleCrearCompromiso = async () => {
    if (!nuevoCompromiso.cliente_id || !nuevoCompromiso.monto_comprometido || !nuevoCompromiso.fecha_compromiso) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    try {
      const response = await api.post('/finanzas/cobranza/compromisos', nuevoCompromiso);
      toast.success('Compromiso registrado exitosamente');
      setShowNuevoCompromiso(false);
      setNuevoCompromiso({
        cliente_id: '',
        cliente_nombre: '',
        monto_comprometido: '',
        fecha_compromiso: '',
        observaciones: ''
      });
      fetchCompromisosCobranza();
      fetchGestionesCobranza(); // También actualiza gestiones porque se crea una automáticamente
    } catch (error) {
      console.error('Error al crear compromiso:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar compromiso');
    }
  };
  
  // Actualizar estado de compromiso
  const handleActualizarCompromiso = async (compromisoId, estado, montoP, notas) => {
    try {
      const params = { estado };
      if (montoP) params.monto_pagado = montoP;
      if (notas) params.notas_seguimiento = notas;
      
      await api.put(`/finanzas/cobranza/compromisos/${compromisoId}`, null, { params });
      toast.success(`Compromiso marcado como ${estado}`);
      fetchCompromisosCobranza();
      fetchGestionesCobranza();
    } catch (error) {
      console.error('Error al actualizar compromiso:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar compromiso');
    }
  };

  // ==================== FUNCIONES ALERTAS DE COBRANZA (ETAPA 7) ====================

  // Fetch panel de alertas de cobranza
  const fetchAlertasCobranza = async () => {
    setLoadingAlertas(true);
    try {
      const response = await api.get('/finanzas/cobranza/alertas/panel');
      setAlertasCobranza(response.data);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
      setAlertasCobranza(null);
    } finally {
      setLoadingAlertas(false);
    }
  };

  // Generar alertas de cobranza
  const handleGenerarAlertas = async () => {
    setGenerandoAlertas(true);
    try {
      const response = await api.post('/finanzas/cobranza/alertas/generar');
      if (response.data.alertas_generadas > 0) {
        toast.success(`Se generaron ${response.data.alertas_generadas} nuevas alertas`);
      } else {
        toast.info('No hay nuevas alertas para generar');
      }
      fetchAlertasCobranza();
    } catch (error) {
      console.error('Error al generar alertas:', error);
      toast.error('Error al generar alertas');
    } finally {
      setGenerandoAlertas(false);
    }
  };

  // Marcar alerta como revisada/gestionada
  const handleActualizarAlerta = async (alertaId, estado) => {
    try {
      await api.put(`/finanzas/cobranza/alertas/${alertaId}`, null, { params: { estado } });
      toast.success(`Alerta marcada como ${estado}`);
      fetchAlertasCobranza();
    } catch (error) {
      console.error('Error al actualizar alerta:', error);
      toast.error('Error al actualizar alerta');
    }
  };

  // Crear gestión desde alerta
  const handleCrearGestionDesdeAlerta = async (alerta, tipoGestion, canal, resumen, resultado) => {
    try {
      const params = {
        tipo_gestion: tipoGestion,
        canal: canal,
        resumen: resumen || `Gestión creada desde alerta: ${alerta.mensaje}`
      };
      if (resultado) params.resultado = resultado;

      await api.post(`/finanzas/cobranza/alertas/${alerta.id}/crear-gestion`, null, { params });
      toast.success('Gestión creada y alerta gestionada');
      setShowCrearGestionDesdeAlerta(false);
      setAlertaSeleccionada(null);
      fetchAlertasCobranza();
      fetchGestionesCobranza();
    } catch (error) {
      console.error('Error al crear gestión desde alerta:', error);
      toast.error('Error al crear gestión');
    }
  };

  // Fetch historial de alertas
  const fetchHistorialAlertas = async () => {
    try {
      const response = await api.get('/finanzas/cobranza/alertas/historial', { params: { limit: 100 } });
      setHistorialAlertas(response.data.alertas || []);
    } catch (error) {
      console.error('Error al cargar historial de alertas:', error);
      setHistorialAlertas([]);
    }
  };
  
  // Ver historial de gestiones de un cliente
  const handleVerHistorialGestiones = async (clienteId) => {
    try {
      const response = await api.get(`/finanzas/cobranza/gestiones/cliente/${clienteId}`);
      setHistorialGestionesData(response.data);
      setShowHistorialGestiones(true);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast.error('Error al cargar historial de gestiones');
    }
  };
  
  // Obtener lista de clientes con facturas para gestiones
  const clientesConFacturas = useMemo(() => {
    const clientesMap = new Map();
    
    // Agregar clientes de facturas en cobro
    cobranzasData.facturas?.forEach(f => {
      if (f.cliente?.id) {
        clientesMap.set(f.cliente.id, {
          id: f.cliente.id,
          name: f.cliente.company || f.cliente.name,
          rut: f.cliente.rut,
          deuda: (clientesMap.get(f.cliente.id)?.deuda || 0) + (f.pending_amount_clp || 0)
        });
      }
    });
    
    return Array.from(clientesMap.values()).sort((a, b) => b.deuda - a.deuda);
  }, [cobranzasData.facturas]);
  
  // Fetch clientes para Estado de Cuenta
  const fetchClientesEstadoCuenta = async () => {
    try {
      const response = await api.get('/finanzas/estados-cuenta/clientes');
      setClientesEstadoCuenta(response.data.clientes || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClientesEstadoCuenta([]);
    }
  };
  
  // Generar Estado de Cuenta
  const handleGenerarEstadoCuenta = async () => {
    if (!filtrosEstadoCuenta.client_id) {
      toast.error('Seleccione un cliente');
      return;
    }
    
    setLoadingEstadoCuenta(true);
    try {
      const response = await api.post('/finanzas/estados-cuenta/generar', filtrosEstadoCuenta);
      setEstadoCuentaData(response.data);
      toast.success('Estado de cuenta generado');
    } catch (error) {
      console.error('Error al generar estado de cuenta:', error);
      toast.error(error.response?.data?.detail || 'Error al generar estado de cuenta');
      setEstadoCuentaData(null);
    } finally {
      setLoadingEstadoCuenta(false);
    }
  };

  // Ver PDF del Estado de Cuenta
  const handleVerPDFEstadoCuenta = () => {
    if (!filtrosEstadoCuenta.client_id) {
      toast.error('Seleccione un cliente');
      return;
    }
    
    // Construir URL del PDF
    const params = new URLSearchParams({
      client_id: filtrosEstadoCuenta.client_id,
      fecha_desde: filtrosEstadoCuenta.fecha_desde,
      fecha_hasta: filtrosEstadoCuenta.fecha_hasta,
      estado_filtro: filtrosEstadoCuenta.estado_filtro
    });
    
    // Obtener token de autenticación
    const token = localStorage.getItem('token');
    
    // Abrir PDF en nueva pestaña usando fetch para autenticar
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/finanzas/estados-cuenta/pdf`;
    
    fetch(pdfUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(filtrosEstadoCuenta)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Error al generar PDF');
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Limpiar URL después de un tiempo
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    })
    .catch(error => {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el PDF');
    });
  };

  // Fetch valor de la UF del día
  const fetchValorUF = async () => {
    setLoadingUF(true);
    try {
      const response = await fetch('https://mindicador.cl/api/uf');
      const data = await response.json();
      if (data.serie && data.serie.length > 0) {
        setValorUF(data.serie[0].valor);
      }
    } catch (error) {
      console.error('Error al obtener valor UF:', error);
      // Valor por defecto si falla la API
      setValorUF(40000);
    } finally {
      setLoadingUF(false);
    }
  };

  // Fetch datos de ingresos proyectados
  const fetchIngresosData = async () => {
    setLoadingIngresos(true);
    try {
      const [ingresosRes, areasRes] = await Promise.all([
        api.get('/finanzas/ingresos-proyectados'),
        api.get('/finanzas/areas-ingresos')
      ]);
      setIngresosData(ingresosRes.data);
      setAreasDisponibles(areasRes.data);
    } catch (error) {
      console.error('Error al cargar ingresos:', error);
    } finally {
      setLoadingIngresos(false);
    }
  };

  // Fetch datos de proyección de facturación (12 meses)
  const fetchProyeccionData = async () => {
    setLoadingProyeccion(true);
    try {
      const response = await api.get('/finanzas/proyeccion-facturacion?meses=12');
      setProyeccionData(response.data);
    } catch (error) {
      console.error('Error al cargar proyección:', error);
    } finally {
      setLoadingProyeccion(false);
    }
  };

  // Fetch datos de cobranzas (facturas en cobro)
  const fetchCobranzasData = async () => {
    setLoadingCobranzas(true);
    try {
      const response = await api.get('/finanzas/cobranzas-facturas');
      setCobranzasData(response.data);
    } catch (error) {
      console.error('Error al cargar cobranzas:', error);
    } finally {
      setLoadingCobranzas(false);
    }
  };

  // ==================== FUNCIONES DE CONCILIACIÓN BANCARIA ====================
  
  const fetchConciliacionData = async () => {
    setLoadingConciliacion(true);
    try {
      const [resumenRes, cuentasRes, movimientosRes, categoriasRes, categoriasFlat, resumenClasRes] = await Promise.all([
        api.get('/conciliacion/resumen'),
        api.get('/conciliacion/cuentas-bancarias'),
        api.get('/conciliacion/movimientos-clasificados?limit=200'),
        api.get('/conciliacion/categorias'),
        api.get('/conciliacion/categorias/flat'),
        api.get('/conciliacion/resumen-clasificacion')
      ]);
      setConciliacionData(resumenRes.data);
      setCuentasBancarias(cuentasRes.data);
      setMovimientosBancarios(movimientosRes.data);
      setCategorias(categoriasRes.data);
      setCategoriasFlat(categoriasFlat.data);
      setResumenClasificacion(resumenClasRes.data);
    } catch (error) {
      console.error('Error al cargar datos de conciliación:', error);
    } finally {
      setLoadingConciliacion(false);
    }
  };

  // Inicializar categorías si no existen
  const initializeCategorias = async () => {
    try {
      const response = await api.post('/conciliacion/categorias/inicializar');
      if (response.data.created) {
        toast.success(`${response.data.total} categorías creadas`);
        fetchConciliacionData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Error al inicializar categorías');
    }
  };

  // Generar sugerencias de clasificación
  const generarSugerencias = async () => {
    try {
      const response = await api.post('/conciliacion/sugerir-clasificaciones?all_unclassified=true');
      toast.success(`${response.data.suggested} sugerencias generadas de ${response.data.processed} procesados`);
      fetchConciliacionData();
    } catch (error) {
      toast.error('Error al generar sugerencias');
    }
  };

  const fetchHistorialCargas = async () => {
    try {
      const response = await api.get('/conciliacion/cargas?limit=50');
      setHistorialCargas(response.data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    }
  };

  const handleCrearCuenta = async () => {
    if (!nuevaCuenta.bank_name || !nuevaCuenta.account_name || !nuevaCuenta.account_number) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    try {
      await api.post('/conciliacion/cuentas-bancarias', nuevaCuenta);
      toast.success('Cuenta bancaria creada exitosamente');
      setShowNuevaCuenta(false);
      setNuevaCuenta({ bank_name: '', account_name: '', account_number: '', currency: 'CLP', is_active: true });
      fetchConciliacionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al crear cuenta');
    }
  };

  const handleCargarCartola = async () => {
    if (!cuentaSeleccionadaCarga || !archivoCartola) {
      toast.error('Seleccione una cuenta y un archivo');
      return;
    }
    
    setCargandoCartola(true);
    try {
      const formData = new FormData();
      formData.append('bank_account_id', cuentaSeleccionadaCarga);
      formData.append('file', archivoCartola);
      if (periodoReferencia) {
        formData.append('reference_period', periodoReferencia);
      }
      
      const response = await api.post('/conciliacion/cargar-cartola', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Cartola procesada: ${response.data.movements_imported} movimientos importados, ${response.data.movements_duplicated} duplicados omitidos`);
      setShowCargarCartola(false);
      setArchivoCartola(null);
      setCuentaSeleccionadaCarga('');
      setPeriodoReferencia('');
      fetchConciliacionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al cargar cartola');
    } finally {
      setCargandoCartola(false);
    }
  };

  const handleCambiarEstadoMovimiento = async (transactionId, nuevoEstado) => {
    try {
      await api.put(`/conciliacion/movimientos/${transactionId}/estado?nuevo_estado=${nuevoEstado}`);
      toast.success(`Estado actualizado a "${nuevoEstado}"`);
      fetchConciliacionData();
      if (movimientoSeleccionado?.id === transactionId) {
        setMovimientoSeleccionado({ ...movimientoSeleccionado, review_status: nuevoEstado });
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleVerDetalleMovimiento = async (transactionId) => {
    try {
      const response = await api.get(`/conciliacion/movimientos/${transactionId}`);
      setMovimientoSeleccionado(response.data);
      setShowDetalleMovimiento(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const handleVerDetalleCarga = async (uploadId) => {
    try {
      const response = await api.get(`/conciliacion/cargas/${uploadId}`);
      setCargaSeleccionada(response.data);
      setShowDetalleCarga(true);
    } catch (error) {
      toast.error('Error al cargar detalle de carga');
    }
  };

  const handleCrearDatosDemo = async () => {
    try {
      const response = await api.post('/conciliacion/crear-datos-demo');
      if (response.data.created) {
        toast.success('Datos de demostración creados exitosamente');
        fetchConciliacionData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Error al crear datos de demostración');
    }
  };

  // ==================== FUNCIONES DE CLASIFICACIÓN (Etapa 2) ====================
  
  // Abrir modal de clasificación
  const abrirModalClasificar = (movimiento) => {
    setMovimientoAClasificar(movimiento);
    // Pre-llenar con categoría sugerida si existe
    const catId = movimiento.final_category?.id || movimiento.suggested_category?.id || '';
    setClasificacionForm({
      category_id: catId,
      counterparty_name: movimiento.classification?.counterparty_name || '',
      notes: movimiento.classification?.notes || ''
    });
    setShowClasificarModal(true);
  };

  // Guardar clasificación
  const handleGuardarClasificacion = async () => {
    if (!clasificacionForm.category_id) {
      toast.error('Seleccione una categoría');
      return;
    }
    
    setClasificando(true);
    try {
      const params = new URLSearchParams({
        category_id: clasificacionForm.category_id,
        ...(clasificacionForm.counterparty_name && { counterparty_name: clasificacionForm.counterparty_name }),
        ...(clasificacionForm.notes && { notes: clasificacionForm.notes })
      });
      
      await api.post(`/conciliacion/movimientos/${movimientoAClasificar.id}/clasificar?${params.toString()}`);
      toast.success('Movimiento clasificado exitosamente');
      setShowClasificarModal(false);
      setMovimientoAClasificar(null);
      fetchConciliacionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al clasificar');
    } finally {
      setClasificando(false);
    }
  };

  // Aceptar sugerencia del sistema
  const handleAceptarSugerencia = async (transactionId) => {
    try {
      await api.post(`/conciliacion/movimientos/${transactionId}/aceptar-sugerencia`);
      toast.success('Sugerencia aceptada');
      fetchConciliacionData();
    } catch (error) {
      toast.error('Error al aceptar sugerencia');
    }
  };

  // Quitar clasificación
  const handleQuitarClasificacion = async (transactionId) => {
    try {
      await api.delete(`/conciliacion/movimientos/${transactionId}/clasificacion`);
      toast.success('Clasificación eliminada');
      fetchConciliacionData();
    } catch (error) {
      toast.error('Error al quitar clasificación');
    }
  };

  // ==================== FUNCIONES DE REGLAS (Etapa 3) ====================
  
  const fetchReglas = async () => {
    try {
      const [reglasRes, resumenRes] = await Promise.all([
        api.get('/conciliacion/reglas'),
        api.get('/conciliacion/reglas/resumen')
      ]);
      setReglas(reglasRes.data);
      setResumenReglas(resumenRes.data);
    } catch (error) {
      console.error('Error al cargar reglas:', error);
    }
  };

  const handleCrearRegla = async () => {
    if (!reglaForm.text_pattern || !reglaForm.category_id) {
      toast.error('Patrón de texto y categoría son requeridos');
      return;
    }
    
    setCreandoRegla(true);
    try {
      const payload = {
        ...reglaForm,
        amount_min: reglaForm.amount_min ? parseFloat(reglaForm.amount_min) : null,
        amount_max: reglaForm.amount_max ? parseFloat(reglaForm.amount_max) : null,
        movement_type: reglaForm.movement_type || null,
        bank_account_id: reglaForm.bank_account_id || null
      };
      
      await api.post('/conciliacion/reglas', payload);
      toast.success('Regla creada exitosamente');
      setShowCrearRegla(false);
      resetReglaForm();
      fetchReglas();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al crear regla');
    } finally {
      setCreandoRegla(false);
    }
  };

  const handleCrearReglaDesdeMovimiento = (movimiento) => {
    // Pre-llenar formulario con datos del movimiento
    const pattern = movimiento.description_normalized?.toLowerCase().split(' ').slice(0, 3).join(' ') || '';
    setReglaForm({
      text_pattern: pattern,
      match_type: 'contains',
      movement_type: movimiento.movement_type || '',
      category_id: movimiento.final_category?.id || movimiento.suggested_category?.id || '',
      counterparty_name: movimiento.classification?.counterparty_name || '',
      amount_min: '',
      amount_max: '',
      bank_account_id: '',
      auto_apply: true,
      priority: 50,
      is_active: true
    });
    setShowClasificarModal(false);
    setShowCrearRegla(true);
  };

  const handleToggleRegla = async (ruleId) => {
    try {
      await api.put(`/conciliacion/reglas/${ruleId}/toggle`);
      toast.success('Estado de regla actualizado');
      fetchReglas();
    } catch (error) {
      toast.error('Error al actualizar regla');
    }
  };

  const handleEliminarRegla = async (ruleId) => {
    if (!window.confirm('¿Está seguro de eliminar esta regla?')) return;
    try {
      await api.delete(`/conciliacion/reglas/${ruleId}`);
      toast.success('Regla eliminada');
      fetchReglas();
    } catch (error) {
      toast.error('Error al eliminar regla');
    }
  };

  const handleDuplicarRegla = async (ruleId) => {
    try {
      await api.post(`/conciliacion/reglas/${ruleId}/duplicate`);
      toast.success('Regla duplicada (inactiva)');
      fetchReglas();
    } catch (error) {
      toast.error('Error al duplicar regla');
    }
  };

  const handleRecalcularSugerencias = async () => {
    try {
      const response = await api.post('/conciliacion/recalcular-sugerencias?all_pending=true');
      toast.success(`Sugerencias recalculadas: ${response.data.rule_suggestions} por reglas, ${response.data.basic_suggestions} básicas`);
      fetchConciliacionData();
    } catch (error) {
      toast.error('Error al recalcular sugerencias');
    }
  };

  const handleInicializarReglas = async () => {
    try {
      const response = await api.post('/conciliacion/reglas/inicializar-demo');
      if (response.data.created) {
        toast.success(`${response.data.rules_created} reglas creadas`);
        fetchReglas();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Error al inicializar reglas');
    }
  };

  const resetReglaForm = () => {
    setReglaForm({
      text_pattern: '',
      match_type: 'contains',
      movement_type: '',
      category_id: '',
      counterparty_name: '',
      amount_min: '',
      amount_max: '',
      bank_account_id: '',
      auto_apply: true,
      priority: 50,
      is_active: true
    });
  };

  // Cargar reglas cuando se abre el panel
  useEffect(() => {
    if (showReglasPanel) {
      fetchReglas();
    }
  }, [showReglasPanel]);

  // Filtrar movimientos (actualizado para clasificación)
  const movimientosFiltrados = useMemo(() => {
    return movimientosBancarios.filter(mov => {
      // Filtro por cuenta
      if (filtroCuenta !== 'todas' && mov.bank_account_id !== filtroCuenta) return false;
      
      // Filtro por tipo de movimiento
      if (filtroTipoMovimiento !== 'todos' && mov.movement_type !== filtroTipoMovimiento) return false;
      
      // Filtro por estado de revisión
      if (filtroEstadoRevision !== 'todos' && mov.review_status !== filtroEstadoRevision) return false;
      
      // Filtro por estado de clasificación
      if (filtroClasificacion !== 'todos') {
        const status = mov.classification_status || 'unclassified';
        if (filtroClasificacion !== status) return false;
      }
      
      // Filtro por categoría
      if (filtroCategoria !== 'todas') {
        const catId = mov.final_category?.id || mov.suggested_category?.id;
        if (catId !== filtroCategoria) return false;
      }
      
      // Filtro por tipo de categoría
      if (filtroTipoCategoria !== 'todos') {
        const cat = mov.final_category || mov.suggested_category;
        if (!cat || cat.type !== filtroTipoCategoria) return false;
      }
      
      // Filtro por búsqueda
      if (busquedaMovimientos.trim()) {
        const busqueda = busquedaMovimientos.toLowerCase();
        return (
          mov.description_raw?.toLowerCase().includes(busqueda) ||
          mov.description_normalized?.toLowerCase().includes(busqueda) ||
          mov.bank_name?.toLowerCase().includes(busqueda) ||
          mov.account_name?.toLowerCase().includes(busqueda) ||
          mov.external_reference?.toLowerCase().includes(busqueda) ||
          mov.classification?.counterparty_name?.toLowerCase().includes(busqueda) ||
          mov.final_category?.name?.toLowerCase().includes(busqueda) ||
          mov.suggested_category?.name?.toLowerCase().includes(busqueda)
        );
      }
      
      return true;
    });
  }, [movimientosBancarios, filtroCuenta, filtroTipoMovimiento, filtroEstadoRevision, filtroClasificacion, filtroCategoria, filtroTipoCategoria, busquedaMovimientos]);

  // Formatear moneda
  const formatMonto = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);
  };

  // Filtrar cuotas de proyección por búsqueda (RUT o Cliente)
  const cuotasProyeccionFiltradas = proyeccionData.cuotas.filter(cuota => {
    if (!busquedaProyeccion) return true;
    const busqueda = busquedaProyeccion.toLowerCase();
    return (
      cuota.cliente?.name?.toLowerCase().includes(busqueda) ||
      cuota.cliente?.company?.toLowerCase().includes(busqueda) ||
      cuota.cliente?.rut?.toLowerCase().includes(busqueda) ||
      cuota.facturacion?.rut?.toLowerCase().includes(busqueda) ||
      cuota.proposal?.folio?.toLowerCase().includes(busqueda) ||
      cuota.area?.toLowerCase().includes(busqueda)
    );
  });

  // Filtrar cobranzas por búsqueda
  const cobranzasFiltradas = cobranzasData.en_cobro?.filter(factura => {
    if (!busquedaCobranzas) return true;
    const busqueda = busquedaCobranzas.toLowerCase();
    return (
      factura.numero_factura?.toLowerCase().includes(busqueda) ||
      factura.cliente?.name?.toLowerCase().includes(busqueda) ||
      factura.cliente?.company?.toLowerCase().includes(busqueda) ||
      factura.cliente?.rut?.toLowerCase().includes(busqueda) ||
      factura.proposal?.folio?.toLowerCase().includes(busqueda)
    );
  }) || [];

  // Agrupar cuotas de proyección por mes
  const cuotasAgrupadasPorMes = useMemo(() => {
    const grupos = {};
    const mesesNombres = {
      '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
      '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
      '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
    };
    
    cuotasProyeccionFiltradas.forEach(cuota => {
      let mesKey = 'sin-fecha';
      let mesNombre = 'Sin Fecha Definida';
      
      if (cuota.fecha_emision) {
        const fecha = new Date(cuota.fecha_emision);
        const año = fecha.getFullYear();
        const mes = String(fecha.getMonth() + 1).padStart(2, '0');
        mesKey = `${año}-${mes}`;
        mesNombre = `${mesesNombres[mes]} ${año}`;
      }
      
      if (!grupos[mesKey]) {
        grupos[mesKey] = {
          mesKey,
          mesNombre,
          cuotas: [],
          totalUF: 0
        };
      }
      
      grupos[mesKey].cuotas.push(cuota);
      grupos[mesKey].totalUF += cuota.cuota?.monto_uf || 0;
    });
    
    // Ordenar por fecha (mes-año)
    return Object.values(grupos).sort((a, b) => {
      if (a.mesKey === 'sin-fecha') return 1;
      if (b.mesKey === 'sin-fecha') return -1;
      return a.mesKey.localeCompare(b.mesKey);
    });
  }, [cuotasProyeccionFiltradas]);

  // Helper para formatear fecha en español
  const formatearFechaEspanol = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr + 'T00:00:00');
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${fecha.getDate()} de ${meses[fecha.getMonth()]} de ${fecha.getFullYear()}`;
  };

  // Generar descripción base (sin fecha de vencimiento)
  const generarDescripcionBase = (cuota) => {
    const numeroContrato = cuota?.proposal?.numero_contrato || cuota?.proposal?.folio || 'N/A';
    const fechaContrato = cuota?.proposal?.fecha_contrato_formatted || '';
    const numeroCuota = cuota?.cuota?.numero || 1;
    const totalCuotas = cuota?.proposal?.total_cuotas || '?';
    return `Servicios según Contrato N° ${numeroContrato}, de fecha ${fechaContrato}. Cuota ${numeroCuota} de ${totalCuotas}.`;
  };

  // Abrir modal de facturación
  const abrirModalFacturar = (cuota) => {
    setCuotaAFacturar(cuota);
    // Calcular monto sugerido: UF de la cuota * valor UF del día
    const ufCuota = cuota.cuota?.monto_uf || 0;
    const montoSugerido = valorUF ? Math.round(ufCuota * valorUF) : (cuota.cuota?.monto_clp || '');
    const hoy = new Date().toISOString().slice(0, 10);
    
    // Servicio por defecto de la PSP
    const servicioPorDefecto = 'Asesoría profesional';
    
    // Descripción con fecha de vencimiento (por defecto hoy)
    const descripcionBase = generarDescripcionBase(cuota);
    const fechaVencFormateada = formatearFechaEspanol(hoy);
    const descripcionCompleta = `${descripcionBase} Fecha de vencimiento: ${fechaVencFormateada}.`;
    
    setDatosFactura({
      numero_factura: '',
      fecha_emision: hoy,
      fecha_vencimiento: hoy, // Por defecto mismo día de emisión
      monto_facturado: montoSugerido,
      servicio: servicioPorDefecto,
      descripcion: descripcionCompleta
    });
    setShowFacturarModal(true);
  };

  // Actualizar descripción cuando cambia la fecha de vencimiento
  const handleFechaVencimientoChange = (nuevaFecha) => {
    const descripcionBase = generarDescripcionBase(cuotaAFacturar);
    const fechaVencFormateada = formatearFechaEspanol(nuevaFecha);
    const descripcionCompleta = `${descripcionBase} Fecha de vencimiento: ${fechaVencFormateada}.`;
    
    setDatosFactura(prev => ({
      ...prev,
      fecha_vencimiento: nuevaFecha,
      descripcion: descripcionCompleta
    }));
  };

  // Procesar facturación
  const handleFacturar = async () => {
    if (!datosFactura.numero_factura || !datosFactura.fecha_emision || !datosFactura.fecha_vencimiento || !datosFactura.monto_facturado) {
      toast.error('Complete todos los campos');
      return;
    }

    setFacturando(true);
    try {
      await api.post(
        `/finanzas/cuotas/${cuotaAFacturar.proposal.id}/${cuotaAFacturar.cuota.index}/facturar`,
        {
          numero_factura: datosFactura.numero_factura,
          fecha_emision: datosFactura.fecha_emision,
          fecha_vencimiento: datosFactura.fecha_vencimiento,
          monto_facturado: parseFloat(datosFactura.monto_facturado)
        }
      );
      toast.success(`Factura #${datosFactura.numero_factura} creada exitosamente`);
      setShowFacturarModal(false);
      setCuotaAFacturar(null);
      setCuotaSeleccionada(null);
      // Refrescar datos
      fetchProyeccionData();
      fetchCobranzasData();
      fetchIngresosData();
      fetchData();
    } catch (error) {
      toast.error('Error al crear factura');
      console.error(error);
    } finally {
      setFacturando(false);
    }
  };

  // Marcar factura como pagada
  const handleMarcarPagada = async (cobranzaId) => {
    try {
      await api.put(`/finanzas/cobranzas-facturas/${cobranzaId}/pagar`);
      toast.success('Factura marcada como pagada');
      fetchCobranzasData();
      fetchProyeccionData();
      fetchIngresosData();
      fetchData();
    } catch (error) {
      toast.error('Error al marcar como pagada');
    }
  };
  
  // Abrir modal de registro de pago (Opera en CLP)
  const handleAbrirRegistrarPago = async (factura) => {
    setFacturaSeleccionadaPago(factura);
    const pendingClp = factura.pending_amount_clp ?? factura.total_amount_clp ?? factura.monto_clp ?? 0;
    setNuevoPago({
      amount_clp: pendingClp.toString(),
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'transferencia',
      reference: '',
      notes: ''
    });
    setShowRegistrarPago(true);
    await fetchHistorialPagos(factura.id);
  };
  
  // Abrir modal de historial financiero completo (pagos + notas)
  const handleAbrirHistorialFinanciero = async (factura) => {
    setShowHistorialFinanciero(true);
    setLoadingHistorialFinanciero(true);
    try {
      const response = await api.get(`/finanzas/cobranzas-facturas/${factura.id}/historial-financiero`);
      setHistorialFinancieroData(response.data);
    } catch (error) {
      console.error('Error al cargar historial financiero:', error);
      toast.error('Error al cargar historial financiero');
      setHistorialFinancieroData(null);
    } finally {
      setLoadingHistorialFinanciero(false);
    }
  };
  
  // Obtener historial de pagos de una factura
  const fetchHistorialPagos = async (cobranzaId) => {
    setLoadingPagos(true);
    try {
      const response = await api.get(`/finanzas/cobranzas-facturas/${cobranzaId}/pagos`);
      setHistorialPagos(response.data.pagos || []);
    } catch (error) {
      console.error('Error al cargar historial de pagos:', error);
      setHistorialPagos([]);
    } finally {
      setLoadingPagos(false);
    }
  };
  
  // Eliminar pago manual
  const handleEliminarPago = async (paymentId, cobranzaId) => {
    if (!confirm('¿Estás seguro de eliminar este pago? Esta acción recalculará el saldo de la factura.')) {
      return;
    }
    
    try {
      await api.delete(`/finanzas/pagos/${paymentId}`);
      toast.success('Pago eliminado exitosamente');
      // Recargar historial financiero
      if (cobranzaId) {
        const response = await api.get(`/finanzas/cobranzas-facturas/${cobranzaId}/historial-financiero`);
        setHistorialFinancieroData(response.data);
      }
      // Recargar datos de cobranzas
      fetchCobranzasData();
    } catch (error) {
      console.error('Error al eliminar pago:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar pago';
      toast.error(errorMsg);
    }
  };
  
  // Anular nota de crédito o débito
  const handleAnularNota = async (documentId, documentType, cobranzaId) => {
    const tipoNota = documentType === 'credit_note' ? 'Nota de Crédito' : 'Nota de Débito';
    if (!confirm(`¿Estás seguro de anular esta ${tipoNota}? Esta acción recalculará el saldo de la factura.`)) {
      return;
    }
    
    try {
      await api.post(`/finanzas/adjustments/${documentId}/void`, { void_reason: 'Anulada desde historial financiero' });
      toast.success(`${tipoNota} anulada exitosamente`);
      // Recargar historial financiero
      if (cobranzaId) {
        const response = await api.get(`/finanzas/cobranzas-facturas/${cobranzaId}/historial-financiero`);
        setHistorialFinancieroData(response.data);
      }
      // Recargar datos de cobranzas
      fetchCobranzasData();
    } catch (error) {
      console.error('Error al anular nota:', error);
      const errorMsg = error.response?.data?.detail || `Error al anular ${tipoNota}`;
      toast.error(errorMsg);
    }
  };
  
  // Registrar un pago (Opera en CLP)
  const handleRegistrarPago = async () => {
    if (!facturaSeleccionadaPago) return;
    
    const amountClp = parseInt(nuevoPago.amount_clp);
    if (isNaN(amountClp) || amountClp <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    
    const pendienteClp = facturaSeleccionadaPago.pending_amount_clp ?? facturaSeleccionadaPago.total_amount_clp ?? facturaSeleccionadaPago.monto_clp ?? 0;
    if (amountClp > pendienteClp + 100) { // Tolerancia $100
      toast.error(`El monto no puede exceder el pendiente ($${pendienteClp.toLocaleString('es-CL')})`);
      return;
    }
    
    try {
      await api.post(`/finanzas/cobranzas-facturas/${facturaSeleccionadaPago.id}/pagos`, {
        cobranza_id: facturaSeleccionadaPago.id,
        amount_clp: amountClp,
        payment_date: nuevoPago.payment_date,
        payment_method: nuevoPago.payment_method,
        reference: nuevoPago.reference || null,
        notes: nuevoPago.notes || null
      });
      
      toast.success('Pago registrado exitosamente');
      
      // Si el monto pagado cubre el total, cerrar modal
      if (Math.abs(amountClp - pendienteClp) < 100) {
        setShowRegistrarPago(false);
        setFacturaSeleccionadaPago(null);
      } else {
        // Refrescar datos del modal para pagos parciales
        await fetchHistorialPagos(facturaSeleccionadaPago.id);
        // Actualizar factura seleccionada
        const cobranzasRes = await api.get('/finanzas/cobranzas-facturas');
        const facturaActualizada = cobranzasRes.data.facturas.find(f => f.id === facturaSeleccionadaPago.id);
        if (facturaActualizada) {
          setFacturaSeleccionadaPago(facturaActualizada);
          setNuevoPago(prev => ({
            ...prev,
            amount_clp: (facturaActualizada.pending_amount_clp || '').toString()
          }));
        }
      }
      
      // Refrescar datos globales
      fetchCobranzasData();
      fetchProyeccionData();
      fetchIngresosData();
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al registrar pago');
      console.error(error);
    }
  };

  // ==================== FUNCIONES DE NOTAS DE CRÉDITO/DÉBITO ====================

  // Abrir modal de nota de crédito
  const handleAbrirNotaCredito = async (factura) => {
    setFacturaNotaCredito(factura);
    setNotaCreditoForm({
      document_number: '',
      issue_date: new Date().toISOString().slice(0, 10),
      amount_clp: '',
      reason: '',
      reference_text: '',
      notes: ''
    });
    await fetchDocumentosAjuste(factura.id);
    setShowNotaCredito(true);
  };

  // Abrir modal de nota de débito
  const handleAbrirNotaDebito = async (factura) => {
    setFacturaNotaDebito(factura);
    setNotaDebitoForm({
      document_number: '',
      issue_date: new Date().toISOString().slice(0, 10),
      amount_clp: '',
      reason: '',
      reference_text: '',
      notes: ''
    });
    await fetchDocumentosAjuste(factura.id);
    setShowNotaDebito(true);
  };

  // Obtener documentos de ajuste de una factura
  const fetchDocumentosAjuste = async (invoiceId) => {
    setLoadingDocumentos(true);
    try {
      const response = await api.get(`/finanzas/cobranzas-facturas/${invoiceId}/documentos-ajuste?include_voided=true`);
      setDocumentosAjuste(response.data || []);
    } catch (error) {
      console.error('Error al cargar documentos de ajuste:', error);
      setDocumentosAjuste([]);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Emitir nota de crédito
  const handleEmitirNotaCredito = async () => {
    if (!facturaNotaCredito) return;
    
    const pendienteActual = facturaNotaCredito.pending_amount_clp || facturaNotaCredito.total_amount_clp || 0;
    const montoNota = parseInt(notaCreditoForm.amount_clp) || 0;
    
    if (montoNota <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    if (montoNota > pendienteActual) {
      toast.error(`El monto no puede exceder el saldo pendiente ($${pendienteActual.toLocaleString('es-CL')})`);
      return;
    }
    if (!notaCreditoForm.document_number) {
      toast.error('Debe ingresar el número de la nota de crédito');
      return;
    }
    if (!notaCreditoForm.reason) {
      toast.error('Debe ingresar el motivo de la nota de crédito');
      return;
    }

    try {
      await api.post(`/finanzas/cobranzas-facturas/${facturaNotaCredito.id}/documentos-ajuste`, {
        invoice_id: facturaNotaCredito.id,
        document_type: 'credit_note',
        document_number: notaCreditoForm.document_number,
        issue_date: notaCreditoForm.issue_date,
        amount_clp: montoNota,
        reason: notaCreditoForm.reason,
        reference_text: notaCreditoForm.reference_text || null,
        notes: notaCreditoForm.notes || null
      });
      
      toast.success('Nota de Crédito emitida exitosamente');
      setShowNotaCredito(false);
      fetchCobranzasData();
      fetchProyeccionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al emitir nota de crédito');
    }
  };

  // Emitir nota de débito
  const handleEmitirNotaDebito = async () => {
    if (!facturaNotaDebito) return;
    
    const montoNota = parseInt(notaDebitoForm.amount_clp) || 0;
    
    if (montoNota <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    if (!notaDebitoForm.document_number) {
      toast.error('Debe ingresar el número de la nota de débito');
      return;
    }
    if (!notaDebitoForm.reason) {
      toast.error('Debe ingresar el motivo de la nota de débito');
      return;
    }

    try {
      await api.post(`/finanzas/cobranzas-facturas/${facturaNotaDebito.id}/documentos-ajuste`, {
        invoice_id: facturaNotaDebito.id,
        document_type: 'debit_note',
        document_number: notaDebitoForm.document_number,
        issue_date: notaDebitoForm.issue_date,
        amount_clp: montoNota,
        reason: notaDebitoForm.reason,
        reference_text: notaDebitoForm.reference_text || null,
        notes: notaDebitoForm.notes || null
      });
      
      toast.success('Nota de Débito emitida exitosamente');
      setShowNotaDebito(false);
      fetchCobranzasData();
      fetchProyeccionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al emitir nota de débito');
    }
  };

  // Anular documento de ajuste
  const handleAnularDocumento = async (documentoId, tipoDocumento) => {
    const tipoNombre = tipoDocumento === 'credit_note' ? 'Nota de Crédito' : 'Nota de Débito';
    
    if (!window.confirm(`¿Está seguro de anular esta ${tipoNombre}? Esta acción recalculará el saldo de la factura.`)) {
      return;
    }

    try {
      await api.put(`/finanzas/documentos-ajuste/${documentoId}/anular`);
      toast.success(`${tipoNombre} anulada exitosamente`);
      
      // Refrescar documentos y cobranzas
      if (facturaNotaCredito) {
        await fetchDocumentosAjuste(facturaNotaCredito.id);
      } else if (facturaNotaDebito) {
        await fetchDocumentosAjuste(facturaNotaDebito.id);
      }
      fetchCobranzasData();
      fetchProyeccionData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al anular documento');
    }
  };

  // Función para actualizar estado de cuota
  const handleCambiarEstadoCuota = async (proposalId, cuotaIndex, nuevoEstado) => {
    try {
      await api.put(`/finanzas/cuotas/${proposalId}/${cuotaIndex}/estado?nuevo_estado=${encodeURIComponent(nuevoEstado)}`);
      toast.success(`Cuota marcada como "${nuevoEstado}"`);
      fetchIngresosData();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado de cuota');
    }
  };

  // Función para visualizar PSP PDF (abre en nueva ventana)
  const handleViewPSP = async (proposalId, folio) => {
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al visualizar PSP:', error);
      toast.error('Error al visualizar PSP');
    }
  };

  // Función para visualizar CPS PDF (abre en nueva ventana)
  const handleViewCPS = async (proposalId, folio) => {
    try {
      const response = await api.post(`/proposals/${proposalId}/generate-cps`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al visualizar CPS:', error);
      toast.error('Error al visualizar CPS');
    }
  };

  // Función para actualizar estado desde la proyección de facturación
  const handleCambiarEstadoProyeccion = async (proposalId, cuotaIndex, nuevoEstado) => {
    try {
      await api.put(`/finanzas/cuotas/${proposalId}/${cuotaIndex}/estado?nuevo_estado=${encodeURIComponent(nuevoEstado)}`);
      toast.success(`Cuota marcada como "${nuevoEstado}"`);
      fetchProyeccionData();
      fetchIngresosData();
      fetchData();
      setCuotaSeleccionada(null);
    } catch (error) {
      toast.error('Error al actualizar estado de cuota');
    }
  };
  
  // ==================== FUNCIONES CONCILIACIÓN FACTURAS (ETAPA 4 - Opera en CLP) ====================
  
  // Abrir modal de conciliación de factura
  const handleAbrirConciliarFactura = async (movimiento) => {
    if (movimiento.movement_type !== 'credit') {
      toast.error('Solo se pueden conciliar movimientos tipo Abono');
      return;
    }
    
    if (movimiento.review_status === 'reconciled') {
      toast.info('Este movimiento ya está conciliado');
      return;
    }
    
    setMovimientoAConciliar(movimiento);
    setShowConciliarFactura(true);
    setLoadingSugerencias(true);
    setSugerenciasConciliacion([]);
    setFacturaSeleccionadaConciliar(null);
    setMontoConciliar('');
    
    try {
      const response = await api.get(`/conciliacion/movimientos/${movimiento.id}/sugerencias-facturas`);
      setSugerenciasConciliacion(response.data.suggestions || []);
      
      if (response.data.already_reconciled) {
        toast.info('Este movimiento ya está conciliado');
        setShowConciliarFactura(false);
      }
    } catch (error) {
      toast.error('Error al cargar sugerencias de conciliación');
      console.error(error);
    } finally {
      setLoadingSugerencias(false);
    }
  };
  
  // Seleccionar factura para conciliar (CLP)
  const handleSeleccionarFacturaConciliar = (factura) => {
    setFacturaSeleccionadaConciliar(factura);
    setMontoConciliar((factura.suggested_amount_clp || factura.pending_amount_clp || 0).toString());
  };
  
  // Confirmar conciliación (CLP)
  const handleConfirmarConciliacion = async () => {
    if (!movimientoAConciliar || !facturaSeleccionadaConciliar) {
      toast.error('Seleccione una factura para conciliar');
      return;
    }
    
    const montoClp = parseInt(montoConciliar);
    if (isNaN(montoClp) || montoClp <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    
    if (montoClp > facturaSeleccionadaConciliar.pending_amount_clp + 100) {
      toast.error(`El monto no puede exceder el pendiente ($${facturaSeleccionadaConciliar.pending_amount_clp.toLocaleString('es-CL')})`);
      return;
    }
    
    try {
      const response = await api.post(`/conciliacion/movimientos/${movimientoAConciliar.id}/conciliar-factura`, {
        bank_transaction_id: movimientoAConciliar.id,
        cobranza_id: facturaSeleccionadaConciliar.cobranza_id,
        amount_clp: montoClp
      });
      
      toast.success(response.data.message || 'Conciliación realizada exitosamente');
      
      // Cerrar modal y refrescar datos
      setShowConciliarFactura(false);
      setMovimientoAConciliar(null);
      setFacturaSeleccionadaConciliar(null);
      
      // Refrescar todos los datos relacionados
      fetchConciliacionData();
      fetchCobranzasData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al conciliar');
      console.error(error);
    }
  };

  // Filtrar propuestas por área y por búsqueda de cliente
  const propuestasFiltradas = useMemo(() => {
    let resultado = ingresosData.propuestas;
    
    // Filtro por área
    if (filtroArea !== 'todas') {
      resultado = resultado.filter(p => p.area === filtroArea);
    }
    
    // Filtro por búsqueda de cliente
    if (busquedaCliente.trim()) {
      const busqueda = busquedaCliente.toLowerCase().trim();
      resultado = resultado.filter(p => {
        const nombreCliente = (p.datos_facturacion?.nombre_razon_social || p.client?.company || p.client?.name || '').toLowerCase();
        const rutCliente = (p.datos_facturacion?.rut || p.client?.rut || '').toLowerCase();
        return nombreCliente.includes(busqueda) || rutCliente.includes(busqueda);
      });
    }
    
    return resultado;
  }, [ingresosData.propuestas, filtroArea, busquedaCliente]);

  const fetchData = async () => {
    try {
      const { values, fallidos } = await settleRequests({
        'propuestas-aceptadas':  api.get('/finanzas/propuestas-aceptadas'),
        'gastos':                api.get(`/finanzas/gastos?mes=${selectedMes}`),
        'cajas-fondo':           api.get('/finanzas/cajas-fondo'),
        'presupuesto':           api.get(`/finanzas/presupuesto/${selectedMes}`),
      });
      if (values['propuestas-aceptadas']) setPropuestasAceptadas(values['propuestas-aceptadas'].data);
      if (values['gastos'])               setGastos(values['gastos'].data);
      if (values['cajas-fondo'])          setCajasFondo(values['cajas-fondo'].data);
      if (values['presupuesto'])          setPresupuesto(values['presupuesto'].data);
      if (fallidos.length) {
        console.error('[Finanzas.fetchData] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Finanzas · ${f.detalle}`, { duration: 8000 }));
      }
    } catch (error) {
      console.error('[Finanzas.fetchData] Error inesperado:', error);
      toast.error('Error inesperado cargando Finanzas');
    }
  };

  // Funciones para Minutas de Cobranzas
  const fetchMinutasData = async () => {
    setLoadingMinutas(true);
    try {
      const { values, fallidos } = await settleRequests({
        'minutas-cobranza':       api.get('/finanzas/minutas-cobranza?limite=20'),
        'clientes-morosos':       api.get('/finanzas/clientes-morosos'),
        'proxima-reunion':        api.get('/finanzas/proxima-reunion-cobranza'),
        'users':                  api.get('/users'),
      });
      if (values['minutas-cobranza']) setMinutasCobranza(values['minutas-cobranza'].data);
      if (values['clientes-morosos']) setClientesMorosos(values['clientes-morosos'].data);
      if (values['proxima-reunion'])  setProximaReunion(values['proxima-reunion'].data);
      if (values['users'])            setUsuarios(values['users'].data);
      if (fallidos.length) {
        console.error('[Finanzas.fetchMinutasData] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Cobranzas · ${f.detalle}`, { duration: 8000 }));
      }
    } catch (error) {
      console.error('[Finanzas.fetchMinutasData] Error inesperado:', error);
    } finally {
      setLoadingMinutas(false);
    }
  };

  const handleOpenNewMinuta = () => {
    // Calcular próximo lunes
    const hoy = new Date();
    const diasHastaLunes = (8 - hoy.getDay()) % 7 || 7;
    const proximoLunes = new Date(hoy);
    proximoLunes.setDate(hoy.getDate() + diasHastaLunes);
    
    // Preparar items con clientes morosos
    const items = clientesMorosos.map(cliente => ({
      client_id: cliente.client_id,
      client_name: cliente.client_name,
      client_rut: cliente.client_rut,
      monto_adeudado: cliente.monto_adeudado,
      cuotas_vencidas: cliente.cuotas_vencidas,
      dias_mora: cliente.dias_mora_max,
      decision: 'Llamar',
      responsable: '',
      responsable_id: '',
      fecha_compromiso: '',
      observaciones: '',
      psp_relacionados: cliente.psp_relacionados || []
    }));

    setNewMinuta({
      fecha_reunion: new Date().toISOString().slice(0, 10),
      hora_reunion: '10:00',
      asistentes: [],
      asistentes_nombres: [],
      items: items,
      observaciones_generales: '',
      proxima_reunion: proximoLunes.toISOString().slice(0, 10)
    });
    setShowNewMinuta(true);
  };

  const handleSaveMinuta = async () => {
    try {
      if (selectedMinuta?.id) {
        // Actualizar minuta existente
        await api.put(`/finanzas/minutas-cobranza/${selectedMinuta.id}`, newMinuta);
        toast.success('Minuta actualizada');
      } else {
        // Crear nueva minuta
        await api.post('/finanzas/minutas-cobranza', newMinuta);
        toast.success('Minuta creada exitosamente');
      }
      setShowNewMinuta(false);
      setSelectedMinuta(null);
      fetchMinutasData();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al guardar minuta');
    }
  };

  const handleViewMinuta = (minuta) => {
    setSelectedMinuta(minuta);
    setShowViewMinuta(true);
  };

  const handleEditMinuta = (minuta) => {
    setSelectedMinuta(minuta);
    setNewMinuta({
      fecha_reunion: minuta.fecha_reunion,
      hora_reunion: minuta.hora_reunion || '10:00',
      asistentes: minuta.asistentes || [],
      asistentes_nombres: minuta.asistentes_nombres || [],
      // Preservar client_id original para detectar cambios de RUT y advertir al usuario
      items: (minuta.items || []).map(it => ({
        ...it,
        _original_client_id: it.client_id,
        _original_rut_normalizado: it.rut_normalizado || null,
      })),
      observaciones_generales: minuta.observaciones_generales || '',
      proxima_reunion: minuta.proxima_reunion || ''
    });
    setShowNewMinuta(true);
    // Asegurar que la lista de clientes esté disponible para el selector inline
    if ((clientesEstadoCuenta || []).length === 0) {
      fetchClientesEstadoCuenta();
    }
  };

  const handleDeleteMinuta = async (minutaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta minuta?')) return;
    
    try {
      await api.delete(`/finanzas/minutas-cobranza/${minutaId}`);
      toast.success('Minuta eliminada');
      fetchMinutasData();
    } catch (error) {
      toast.error('Error al eliminar minuta');
    }
  };

  const updateMinutaItem = (index, field, value) => {
    setNewMinuta(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      return { ...prev, items: updatedItems };
    });
  };

  const toggleAsistente = (userId, userName) => {
    const asistentes = [...newMinuta.asistentes];
    const nombres = [...newMinuta.asistentes_nombres];
    const idx = asistentes.indexOf(userId);
    
    if (idx > -1) {
      asistentes.splice(idx, 1);
      nombres.splice(idx, 1);
    } else {
      asistentes.push(userId);
      nombres.push(userName);
    }
    
    setNewMinuta({ ...newMinuta, asistentes, asistentes_nombres: nombres });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  const handleCreateGasto = async () => {
    try {
      await api.post('/finanzas/gastos', {
        ...newGasto,
        monto: parseFloat(newGasto.monto),
        fecha: new Date(newGasto.fecha).toISOString()
      });
      toast.success('Gasto registrado');
      setShowNewGasto(false);
      setNewGasto({
        tipo: 'Fijo',
        categoria: '',
        descripcion: '',
        monto: '',
        fecha: new Date().toISOString().slice(0, 10)
      });
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleCreateCaja = async () => {
    try {
      await api.post('/finanzas/cajas-fondo', {
        ...newCaja,
        monto: parseFloat(newCaja.monto),
        fecha_entrega: new Date(newCaja.fecha_entrega).toISOString()
      });
      toast.success('Caja de fondo creada');
      setShowNewCaja(false);
      setNewCaja({
        responsable: '',
        monto: '',
        descripcion: '',
        fecha_entrega: new Date().toISOString().slice(0, 10)
      });
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleRendirCaja = async (cajaId) => {
    try {
      await api.put(`/finanzas/cajas-fondo/${cajaId}/rendir`);
      toast.success('Caja de fondo rendida');
      fetchData();
    } catch (error) {
      toast.error('Error al rendir caja');
    }
  };

  return (
    <div className='min-h-screen bg-slate-50 flex'>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className='flex-1 p-8 overflow-y-auto max-h-screen'>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className='mb-8'>
            <div className='flex items-center justify-between'>
              <div>
                <div className='flex items-center gap-3 mb-2'>
                  <BarChart3 className='w-10 h-10 text-primary' strokeWidth={1.5} />
                  <h1 className='text-4xl font-bold text-slate-900' data-testid='finanzas-title'>
                    Finanzas
                  </h1>
                </div>
                <p className='text-lg text-slate-600'>
                  Administración financiera y salud del negocio
                </p>
              </div>
              <div>
                <Label className='text-sm text-slate-600 mb-2 block'>Mes:</Label>
                <Input
                  type='month'
                  value={selectedMes}
                  onChange={(e) => setSelectedMes(e.target.value)}
                  className='w-48'
                  data-testid='month-selector'
                />
              </div>
            </div>
          </div>

          {/* Tabs - Módulos de Finanzas */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <TabsList className='flex flex-wrap gap-1.5 h-auto p-1.5 mb-6 bg-primary/10 border border-primary/20 rounded-lg'>
              {tabPermitido('ingresos') && (
              <TabsTrigger value='ingresos' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <DollarSign className='w-4 h-4' />
                Ingresos
              </TabsTrigger>
              )}
              {tabPermitido('facturacion') && (
              <TabsTrigger value='facturacion' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <Receipt className='w-4 h-4' />
                Facturación
              </TabsTrigger>
              )}
              {tabPermitido('conciliacion') && (
              <TabsTrigger value='conciliacion' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <ArrowLeftRight className='w-4 h-4' />
                Conciliación
              </TabsTrigger>
              )}
              {tabPermitido('caja-chica') && (
              <TabsTrigger value='caja-chica' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <PiggyBank className='w-4 h-4' />
                Caja Chica
              </TabsTrigger>
              )}
              {tabPermitido('flujo-caja') && (
              <TabsTrigger value='flujo-caja' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <TrendingUp className='w-4 h-4' />
                Flujo de Caja
              </TabsTrigger>
              )}
              {tabPermitido('inventarios') && (
              <TabsTrigger value='inventarios' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <Package className='w-4 h-4' />
                Inventarios
              </TabsTrigger>
              )}
              {tabPermitido('rrhh') && (
              <TabsTrigger value='rrhh' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <UserCog className='w-4 h-4' />
                RRHH
              </TabsTrigger>
              )}
              {tabPermitido('gastos') && (
              <TabsTrigger value='gastos' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <CreditCard className='w-4 h-4' />
                Gastos
              </TabsTrigger>
              )}
              {tabPermitido('cobranza') && (
              <TabsTrigger value='cobranza' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <PhoneCall className='w-4 h-4' />
                Cobranza
              </TabsTrigger>
              )}
              {tabPermitido('reembolsos') && (
              <TabsTrigger value='reembolsos' className='flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md text-slate-700 hover:bg-primary/20 transition-colors'>
                <RotateCcw className='w-4 h-4' />
                Reembolsos
              </TabsTrigger>
              )}
            </TabsList>

            {/* INGRESOS - Módulo de Ingresos Proyectados */}
            <TabsContent value='ingresos'>
              <IngresosTab
                busquedaCliente={busquedaCliente}
                setBusquedaCliente={setBusquedaCliente}
                filtroArea={filtroArea}
                setFiltroArea={setFiltroArea}
                areasDisponibles={areasDisponibles}
                ingresosData={ingresosData}
                loadingIngresos={loadingIngresos}
                propuestasFiltradas={propuestasFiltradas}
                expandedPSP={expandedPSP}
                setExpandedPSP={setExpandedPSP}
                handleViewPSP={handleViewPSP}
                handleViewCPS={handleViewCPS}
              />
            </TabsContent>

            {/* Gastos */}
            <TabsContent value='gastos'>
              <GastosRemuneraciones />
            </TabsContent>

            {/* Cajas de Fondo */}
            <TabsContent value='cajas'>
              <CajasFondoTab
                cajasFondo={cajasFondo}
                showNewCaja={showNewCaja}
                setShowNewCaja={setShowNewCaja}
                newCaja={newCaja}
                setNewCaja={setNewCaja}
                handleCreateCaja={handleCreateCaja}
                handleRendirCaja={handleRendirCaja}
              />
            </TabsContent>

            {/* Minutas de Cobranzas */}
            <TabsContent value='cobranza'>
              <CobranzaTab
                cobranzasData={cobranzasData}
                gestionesCobranza={gestionesCobranza}
                alertasCobranza={alertasCobranza}
                minutasCobranza={minutasCobranza}
                clientesMorosos={clientesMorosos}
                proximaReunion={proximaReunion}
                loadingCobranzas={loadingCobranzas}
                loadingMinutas={loadingMinutas}
                busquedaCobranzas={busquedaCobranzas}
                estadoCuentaData={estadoCuentaData}
                comunicacionesPanel={comunicacionesPanel}
                generandoAlertas={generandoAlertas}
                ejecutandoAutomatico={ejecutandoAutomatico}
                filtroGestionCliente={filtroGestionCliente}
                filtroGestionResultado={filtroGestionResultado}
                filtroGestionTipo={filtroGestionTipo}
                filtrosEstadoCuenta={filtrosEstadoCuenta}
                loadingEstadoCuenta={loadingEstadoCuenta}
                loadingGestiones={loadingGestiones}
                loadingAlertas={loadingAlertas}
                loadingComunicaciones={loadingComunicaciones}
                compromisosCobranza={compromisosCobranza}
                setBusquedaCobranzas={setBusquedaCobranzas}
                setAlertaSeleccionada={setAlertaSeleccionada}
                setClienteEnvioSeleccionado={setClienteEnvioSeleccionado}
                setClienteGestionSeleccionado={setClienteGestionSeleccionado}
                setFiltroGestionCliente={setFiltroGestionCliente}
                setFiltroGestionResultado={setFiltroGestionResultado}
                setFiltroGestionTipo={setFiltroGestionTipo}
                setFiltrosEstadoCuenta={setFiltrosEstadoCuenta}
                setNuevaGestion={setNuevaGestion}
                setShowConfigComunicaciones={setShowConfigComunicaciones}
                setShowCrearGestionDesdeAlerta={setShowCrearGestionDesdeAlerta}
                setShowEnviarEstadoCuenta={setShowEnviarEstadoCuenta}
                setShowEnviarRecordatorio={setShowEnviarRecordatorio}
                setShowHistorialAlertas={setShowHistorialAlertas}
                setShowNuevaGestion={setShowNuevaGestion}
                setShowNuevoCompromiso={setShowNuevoCompromiso}
                canalesGestion={canalesGestion}
                clientesConFacturas={clientesConFacturas}
                clientesEstadoCuenta={clientesEstadoCuenta}
                cobranzasFiltradas={cobranzasFiltradas}
                coloresPrioridad={coloresPrioridad}
                estadosComunicacion={estadosComunicacion}
                iconosAlerta={iconosAlerta}
                resultadosGestion={resultadosGestion}
                sugerenciasAccion={sugerenciasAccion}
                tiposComunicacion={tiposComunicacion}
                tiposGestion={tiposGestion}
                fetchAlertasCobranza={fetchAlertasCobranza}
                fetchCobranzasData={fetchCobranzasData}
                fetchCompromisosCobranza={fetchCompromisosCobranza}
                fetchComunicacionesPanel={fetchComunicacionesPanel}
                fetchConfigComunicaciones={fetchConfigComunicaciones}
                fetchGestionesCobranza={fetchGestionesCobranza}
                fetchHistorialAlertas={fetchHistorialAlertas}
                fetchPlantillasCorreo={fetchPlantillasCorreo}
                handleAbrirHistorialFinanciero={handleAbrirHistorialFinanciero}
                handleAbrirNotaCredito={handleAbrirNotaCredito}
                handleAbrirNotaDebito={handleAbrirNotaDebito}
                handleAbrirRegistrarPago={handleAbrirRegistrarPago}
                handleActualizarAlerta={handleActualizarAlerta}
                handleActualizarCompromiso={handleActualizarCompromiso}
                handleDeleteMinuta={handleDeleteMinuta}
                handleEditMinuta={handleEditMinuta}
                handleEjecutarAutomatico={handleEjecutarAutomatico}
                handleGenerarAlertas={handleGenerarAlertas}
                handleGenerarEstadoCuenta={handleGenerarEstadoCuenta}
                handleOpenNewMinuta={handleOpenNewMinuta}
                handleVerHistorialGestiones={handleVerHistorialGestiones}
                handleVerPDFEstadoCuenta={handleVerPDFEstadoCuenta}
                handleViewMinuta={handleViewMinuta}
                formatCurrency={formatCurrency}
                formatRutSinPuntos={formatRutSinPuntos}
              />
            </TabsContent>

            {/* FACTURACIÓN - Proyección de Facturación a Futuro */}
            <TabsContent value='facturacion'>
              <FacturacionTab
                proyeccionData={proyeccionData}
                busquedaProyeccion={busquedaProyeccion}
                setBusquedaProyeccion={setBusquedaProyeccion}
                loadingProyeccion={loadingProyeccion}
                cuotasProyeccionFiltradas={cuotasProyeccionFiltradas}
                cuotasAgrupadasPorMes={cuotasAgrupadasPorMes}
                fetchProyeccionData={fetchProyeccionData}
                cuotaSeleccionada={cuotaSeleccionada}
                setCuotaSeleccionada={setCuotaSeleccionada}
                abrirModalFacturar={abrirModalFacturar}
                handleViewCPS={handleViewCPS}
                formatRutSinPuntos={formatRutSinPuntos}
              />
            </TabsContent>

            {/* CONCILIACIÓN - Módulo en desarrollo */}
            <TabsContent value='conciliacion'>
              <ConciliacionTab
                conciliacionData={conciliacionData}
                cuentasBancarias={cuentasBancarias}
                movimientosBancarios={movimientosBancarios}
                historialCargas={historialCargas}
                busquedaMovimientos={busquedaMovimientos}
                setBusquedaMovimientos={setBusquedaMovimientos}
                filtroCuenta={filtroCuenta}
                setFiltroCuenta={setFiltroCuenta}
                filtroTipoMovimiento={filtroTipoMovimiento}
                setFiltroTipoMovimiento={setFiltroTipoMovimiento}
                filtroClasificacion={filtroClasificacion}
                setFiltroClasificacion={setFiltroClasificacion}
                movimientosFiltrados={movimientosFiltrados}
                periodoReferencia={periodoReferencia}
                setPeriodoReferencia={setPeriodoReferencia}
                categorias={categorias}
                categoriasFlat={categoriasFlat}
                filtroTipoCategoria={filtroTipoCategoria}
                setFiltroTipoCategoria={setFiltroTipoCategoria}
                resumenClasificacion={resumenClasificacion}
                initializeCategorias={initializeCategorias}
                reglas={reglas}
                reglaForm={reglaForm}
                setReglaForm={setReglaForm}
                resetReglaForm={resetReglaForm}
                filtroReglasActivas={filtroReglasActivas}
                setFiltroReglasActivas={setFiltroReglasActivas}
                resumenReglas={resumenReglas}
                showNuevaCuenta={showNuevaCuenta}
                setShowNuevaCuenta={setShowNuevaCuenta}
                nuevaCuenta={nuevaCuenta}
                setNuevaCuenta={setNuevaCuenta}
                showCargarCartola={showCargarCartola}
                setShowCargarCartola={setShowCargarCartola}
                cuentaSeleccionadaCarga={cuentaSeleccionadaCarga}
                setCuentaSeleccionadaCarga={setCuentaSeleccionadaCarga}
                setArchivoCartola={setArchivoCartola}
                archivoCartola={archivoCartola}
                showHistorialCargas={showHistorialCargas}
                setShowHistorialCargas={setShowHistorialCargas}
                showDetalleMovimiento={showDetalleMovimiento}
                setShowDetalleMovimiento={setShowDetalleMovimiento}
                movimientoSeleccionado={movimientoSeleccionado}
                showDetalleCarga={showDetalleCarga}
                setShowDetalleCarga={setShowDetalleCarga}
                cargaSeleccionada={cargaSeleccionada}
                showClasificarModal={showClasificarModal}
                setShowClasificarModal={setShowClasificarModal}
                movimientoAClasificar={movimientoAClasificar}
                clasificacionForm={clasificacionForm}
                setClasificacionForm={setClasificacionForm}
                showReglasPanel={showReglasPanel}
                setShowReglasPanel={setShowReglasPanel}
                showCrearRegla={showCrearRegla}
                setShowCrearRegla={setShowCrearRegla}
                handleCrearCuenta={handleCrearCuenta}
                handleCargarCartola={handleCargarCartola}
                handleVerDetalleCarga={handleVerDetalleCarga}
                handleVerDetalleMovimiento={handleVerDetalleMovimiento}
                handleCambiarEstadoMovimiento={handleCambiarEstadoMovimiento}
                handleAbrirConciliarFactura={handleAbrirConciliarFactura}
                abrirModalClasificar={abrirModalClasificar}
                handleGuardarClasificacion={handleGuardarClasificacion}
                handleQuitarClasificacion={handleQuitarClasificacion}
                handleCrearRegla={handleCrearRegla}
                handleEliminarRegla={handleEliminarRegla}
                handleToggleRegla={handleToggleRegla}
                handleDuplicarRegla={handleDuplicarRegla}
                handleCrearReglaDesdeMovimiento={handleCrearReglaDesdeMovimiento}
                handleAceptarSugerencia={handleAceptarSugerencia}
                handleRecalcularSugerencias={handleRecalcularSugerencias}
                handleInicializarReglas={handleInicializarReglas}
                handleCrearDatosDemo={handleCrearDatosDemo}
                fetchConciliacionData={fetchConciliacionData}
                fetchHistorialCargas={fetchHistorialCargas}
                generarSugerencias={generarSugerencias}
                formatMonto={formatMonto}
                loadingConciliacion={loadingConciliacion}
                cargandoCartola={cargandoCartola}
                clasificando={clasificando}
                creandoRegla={creandoRegla}
              />
            </TabsContent>

            {/* CAJA CHICA - Solicitudes, aprobaciones y rendiciones */}
            <TabsContent value='caja-chica'>
              <CajaChica />
            </TabsContent>

            {/* FLUJO DE CAJA - Dashboard financiero */}
            <TabsContent value='flujo-caja'>
              <FlujoCajaTab presupuesto={presupuesto} />
            </TabsContent>

            {/* INVENTARIOS - Módulo en desarrollo */}
            <TabsContent value='inventarios'>
              <PlaceholderTab
                titulo='Módulo de Inventarios'
                descripcion='Próximamente: Control de activos fijos, equipos de oficina y materiales de trabajo.'
              />
            </TabsContent>

            {/* RRHH - Sub-tabs: Comisión Líderes, Comunicaciones, Vencimientos, Préstamos */}
            <TabsContent value='rrhh'>
              <RRHHTab />
            </TabsContent>

            {/* ESTADOS DE CUENTA - Módulo de consulta de deuda por cliente */}
            {/* REEMBOLSOS - Módulo en desarrollo */}
            <TabsContent value='reembolsos'>
              <PlaceholderTab
                titulo='Módulo de Reembolsos'
                descripcion='Próximamente: Solicitudes de reembolso, aprobaciones y tracking de pagos a colaboradores.'
              />
            </TabsContent>
          </Tabs>

          {/* Modal Nueva/Editar Minuta */}
          <Dialog open={showNewMinuta} onOpenChange={setShowNewMinuta}>
            <DialogContent className='max-w-[95vw] w-[1200px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Users className='w-5 h-5 text-primary' />
                  {selectedMinuta ? 'Editar Minuta de Cobranzas' : 'Nueva Minuta de Cobranzas'}
                </DialogTitle>
                <DialogDescription>
                  Registra las decisiones tomadas para cada cliente moroso
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-6 py-4'>
                {/* Datos de la reunión */}
                <div className='grid grid-cols-4 gap-4'>
                  <div>
                    <Label>Fecha de Reunión</Label>
                    <Input
                      type='date'
                      value={newMinuta.fecha_reunion}
                      onChange={(e) => setNewMinuta({ ...newMinuta, fecha_reunion: e.target.value })}
                      data-testid='minuta-fecha-input'
                    />
                  </div>
                  <div>
                    <Label>Hora</Label>
                    <Input
                      type='time'
                      value={newMinuta.hora_reunion}
                      onChange={(e) => setNewMinuta({ ...newMinuta, hora_reunion: e.target.value })}
                      data-testid='minuta-hora-input'
                    />
                  </div>
                  <div>
                    <Label>Próxima Reunión</Label>
                    <Input
                      type='date'
                      value={newMinuta.proxima_reunion}
                      onChange={(e) => setNewMinuta({ ...newMinuta, proxima_reunion: e.target.value })}
                      data-testid='minuta-proxima-input'
                    />
                  </div>
                  <div>
                    <Label>Asistentes ({newMinuta.asistentes.length})</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder='Seleccionar asistentes' />
                      </SelectTrigger>
                      <SelectContent>
                        {usuarios.map((user) => (
                          <div 
                            key={user.id} 
                            className='flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-100'
                            onClick={() => toggleAsistente(user.id, user.name)}
                          >
                            <Checkbox checked={newMinuta.asistentes.includes(user.id)} />
                            <span className='text-sm'>{user.name}</span>
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    {newMinuta.asistentes_nombres.length > 0 && (
                      <p className='text-xs text-slate-500 mt-1'>
                        {newMinuta.asistentes_nombres.join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Lista de clientes morosos */}
                <div>
                  <Label className='text-base font-semibold mb-3 block'>
                    Clientes a Revisar ({newMinuta.items.length})
                  </Label>
                  
                  {newMinuta.items.length === 0 ? (
                    <Card className='p-8 text-center border-slate-200 bg-slate-50'>
                      <p className='text-slate-600'>No hay clientes morosos para revisar</p>
                    </Card>
                  ) : (
                    <div className='space-y-3 max-h-[400px] overflow-y-auto pr-2'>
                      {newMinuta.items.map((item, index) => (
                        <Card key={item.client_id} className='p-4 border-slate-200'>
                          <div className='grid grid-cols-12 gap-4 items-start'>
                            {/* Info del cliente + editor de RUT */}
                            <div className='col-span-3'>
                              <p className='font-semibold text-slate-900'>{item.client_name}</p>
                              <div className='mt-1' data-testid={`minuta-item-rut-editor-${index}`}>
                                <Label className='text-[10px]'>Cliente / RUT</Label>
                                <Select
                                  value={item.client_id || ''}
                                  onValueChange={(val) => {
                                    const cli = (clientesEstadoCuenta || []).find(c => c.id === val);
                                    if (!cli) return;
                                    const cliName = cli.nombre || cli.name || cli.empresa || '';
                                    const cliRut = cli.rut || '';
                                    updateMinutaItem(index, 'client_id', cli.id);
                                    updateMinutaItem(index, 'client_name', cliName);
                                    updateMinutaItem(index, 'client_rut', cliRut);
                                    updateMinutaItem(index, 'rut_normalizado', cli.rut_normalizado || null);
                                  }}
                                >
                                  <SelectTrigger className='h-8 text-xs'>
                                    <SelectValue placeholder={item.client_rut || item.client_name || 'Seleccionar cliente'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(clientesEstadoCuenta || []).slice(0, 300).map(c => (
                                      <SelectItem key={c.id} value={c.id}>
                                        {(c.nombre || c.name || c.empresa || '(sin nombre)')} · {c.rut || '—'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {item.client_rut && (
                                  <p className='text-[10px] text-slate-500 mt-1'>
                                    RUT: <span className='font-mono'>{item.client_rut}</span>
                                    {item.rut_normalizado && <span className='ml-1 text-slate-400'>(norm: {item.rut_normalizado})</span>}
                                  </p>
                                )}
                                {item._original_client_id && item._original_client_id !== item.client_id && (
                                  <div className='mt-1 p-2 rounded bg-amber-50 border border-amber-300 text-[10px] text-amber-800' data-testid={`minuta-rut-change-warning-${index}`}>
                                    <p className='font-semibold'>⚠ Cambio de cliente/RUT detectado</p>
                                    <p>Los compromisos derivados previamente permanecerán asociados al RUT original. Se generará una alerta de revisión manual al guardar. Un compromiso para el nuevo RUT requiere acción explícita.</p>
                                  </div>
                                )}
                                {item.saldo_consolidado_snapshot && (
                                  <p className='text-[10px] text-slate-500 mt-1'>
                                    Saldo consolidado: {formatCurrency(item.saldo_consolidado_snapshot.saldo_total)} · {item.saldo_consolidado_snapshot.n_facturas_pendientes} facturas · {item.saldo_consolidado_snapshot.dias_mora_mas_antigua} días
                                  </p>
                                )}
                              </div>
                              <div className='flex items-center gap-2 mt-1'>
                                <Badge variant='destructive' className='text-xs'>
                                  {formatCurrency(item.monto_adeudado)}
                                </Badge>
                                <Badge variant='outline' className='text-xs'>
                                  {item.cuotas_vencidas} cuota{item.cuotas_vencidas !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              {item.dias_mora > 0 && (
                                <p className='text-xs text-red-600 mt-1'>
                                  {item.dias_mora} días de mora
                                </p>
                              )}
                            </div>

                            {/* Decisión */}
                            <div className='col-span-2'>
                              <Label className='text-xs'>Decisión</Label>
                              <Select
                                value={item.decision}
                                onValueChange={(val) => updateMinutaItem(index, 'decision', val)}
                              >
                                <SelectTrigger className='h-9'>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {decisionesCobranza.map((d) => (
                                    <SelectItem key={d.value} value={d.value}>
                                      {d.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Responsable */}
                            <div className='col-span-2'>
                              <Label className='text-xs'>Responsable</Label>
                              <Select
                                value={item.responsable_id || ''}
                                onValueChange={(val) => {
                                  const user = usuarios.find(u => u.id === val);
                                  updateMinutaItem(index, 'responsable_id', val);
                                  updateMinutaItem(index, 'responsable', user?.name || '');
                                }}
                              >
                                <SelectTrigger className='h-9'>
                                  <SelectValue placeholder='Seleccionar' />
                                </SelectTrigger>
                                <SelectContent>
                                  {usuarios.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Fecha compromiso */}
                            <div className='col-span-2'>
                              <Label className='text-xs'>Seguimiento</Label>
                              <Input
                                type='date'
                                value={item.fecha_compromiso || ''}
                                onChange={(e) => updateMinutaItem(index, 'fecha_compromiso', e.target.value)}
                                className='h-9'
                              />
                            </div>

                            {/* Observaciones */}
                            <div className='col-span-3'>
                              <Label className='text-xs'>Observaciones</Label>
                              <DebouncedInput
                                value={item.observaciones || ''}
                                onChange={(e) => updateMinutaItem(index, 'observaciones', e.target.value)}
                                placeholder='Notas...'
                                className='h-9'
                              />
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Observaciones generales */}
                <div>
                  <Label>Observaciones Generales de la Reunión</Label>
                  <DebouncedTextarea
                    value={newMinuta.observaciones_generales}
                    onChange={(e) => setNewMinuta({ ...newMinuta, observaciones_generales: e.target.value })}
                    placeholder='Comentarios generales, acuerdos, etc.'
                    rows={3}
                    data-testid='minuta-obs-input'
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant='outline' onClick={() => { setShowNewMinuta(false); setSelectedMinuta(null); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveMinuta} data-testid='save-minuta-btn'>
                  <Save className='w-4 h-4 mr-2' />
                  {selectedMinuta ? 'Actualizar Minuta' : 'Guardar Minuta'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal Ver Minuta */}
          <Dialog open={showViewMinuta} onOpenChange={setShowViewMinuta}>
            <DialogContent className='max-w-[900px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Calendar className='w-5 h-5 text-primary' />
                  Minuta de Cobranzas - {selectedMinuta && new Date(selectedMinuta.fecha_reunion + 'T12:00:00').toLocaleDateString('es-CL', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </DialogTitle>
              </DialogHeader>

              {selectedMinuta && (
                <div className='space-y-6 py-4'>
                  {/* Info de la reunión */}
                  <div className='grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-lg'>
                    <div>
                      <p className='text-xs text-slate-500'>Hora</p>
                      <p className='font-medium'>{selectedMinuta.hora_reunion || '10:00'}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>Asistentes</p>
                      <p className='font-medium'>{selectedMinuta.asistentes_nombres?.join(', ') || 'No registrados'}</p>
                    </div>
                    <div>
                      <p className='text-xs text-slate-500'>Próxima Reunión</p>
                      <p className='font-medium'>
                        {selectedMinuta.proxima_reunion 
                          ? new Date(selectedMinuta.proxima_reunion + 'T12:00:00').toLocaleDateString('es-CL')
                          : 'No programada'}
                      </p>
                    </div>
                  </div>

                  {/* Decisiones por cliente */}
                  <div>
                    <h4 className='font-semibold text-slate-900 mb-3'>Decisiones por Cliente</h4>
                    <div className='space-y-2'>
                      {selectedMinuta.items?.map((item, idx) => (
                        <div key={idx} className='flex items-center justify-between p-3 bg-white border rounded-lg'>
                          <div className='flex-1'>
                            <p className='font-medium text-slate-900'>{item.client_name}</p>
                            <p className='text-sm text-slate-600'>
                              Deuda: {formatCurrency(item.monto_adeudado)} - {item.cuotas_vencidas} cuota(s)
                            </p>
                          </div>
                          <div className='text-right'>
                            <Badge className='mb-1'>{item.decision}</Badge>
                            {item.responsable && (
                              <p className='text-xs text-slate-500'>Responsable: {item.responsable}</p>
                            )}
                            {item.fecha_compromiso && (
                              <p className='text-xs text-slate-500'>
                                Seguimiento: {new Date(item.fecha_compromiso + 'T12:00:00').toLocaleDateString('es-CL')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Observaciones */}
                  {selectedMinuta.observaciones_generales && (
                    <div>
                      <h4 className='font-semibold text-slate-900 mb-2'>Observaciones Generales</h4>
                      <p className='text-slate-600 bg-slate-50 p-3 rounded-lg'>
                        {selectedMinuta.observaciones_generales}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant='outline' onClick={() => setShowViewMinuta(false)}>
                  Cerrar
                </Button>
                <Button onClick={() => { setShowViewMinuta(false); handleEditMinuta(selectedMinuta); }}>
                  <Edit className='w-4 h-4 mr-2' />
                  Editar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* MODAL DE FACTURACIÓN */}
          <Dialog open={showFacturarModal} onOpenChange={setShowFacturarModal}>
            <DialogContent className='sm:max-w-[580px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Receipt className='w-5 h-5 text-blue-600' />
                  Emitir Factura
                </DialogTitle>
              </DialogHeader>
              
              <div className='space-y-4 py-2'>
                {/* DATOS DE FACTURACIÓN - ARRIBA */}
                {cuotaAFacturar && (
                  <div className='bg-slate-100 border border-slate-300 rounded-lg p-4'>
                    <p className='text-xs text-slate-500 font-semibold mb-2'>DATOS DE FACTURACIÓN</p>
                    <div className='grid grid-cols-2 gap-3'>
                      <div>
                        <p className='text-xs text-slate-500'>RUT</p>
                        <p className='font-bold text-slate-900'>{formatRutSinPuntos(cuotaAFacturar.facturacion?.rut)}</p>
                      </div>
                      <div>
                        <p className='text-xs text-slate-500'>Razón Social</p>
                        <p className='font-bold text-slate-900'>{cuotaAFacturar.facturacion?.nombre_razon_social || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* INFO DE LA CUOTA */}
                {cuotaAFacturar && (
                  <div className='bg-indigo-50 border border-indigo-200 rounded-lg p-3'>
                    <div className='flex justify-between items-center'>
                      <div>
                        <p className='text-sm font-bold text-indigo-800'>
                          Cuota {cuotaAFacturar.cuota?.numero} de {cuotaAFacturar.proposal?.total_cuotas || '?'}
                        </p>
                        <p className='text-xs text-indigo-600 font-mono'>{cuotaAFacturar.proposal?.folio}</p>
                        <p className='text-xs text-indigo-500 mt-1'>
                          Contrato: <span className='font-semibold'>{cuotaAFacturar.proposal?.numero_contrato || 'N/A'}</span>
                        </p>
                      </div>
                      <div className='text-right'>
                        <p className='text-lg font-bold text-indigo-800'>{cuotaAFacturar.cuota?.monto_uf} UF</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Valor UF del día */}
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-xs text-blue-600 font-semibold'>VALOR UF DEL DÍA</p>
                      <p className='text-lg font-bold text-blue-800'>
                        {loadingUF ? 'Cargando...' : valorUF ? `$${new Intl.NumberFormat('es-CL').format(valorUF)}` : 'No disponible'}
                      </p>
                    </div>
                    {cuotaAFacturar && valorUF && (
                      <div className='text-right'>
                        <p className='text-xs text-blue-600'>Monto sugerido</p>
                        <p className='text-sm font-semibold text-blue-800'>
                          ${new Intl.NumberFormat('es-CL').format(Math.round(cuotaAFacturar.cuota?.monto_uf * valorUF))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Servicio */}
                <div className='space-y-2'>
                  <Label htmlFor='servicio'>Servicio</Label>
                  <DebouncedInput
                    id='servicio'
                    placeholder='Servicio de Asesoría...'
                    value={datosFactura.servicio}
                    onChange={(e) => setDatosFactura({...datosFactura, servicio: e.target.value})}
                  />
                </div>

                {/* Descripción */}
                <div className='space-y-2'>
                  <Label htmlFor='descripcion'>Descripción</Label>
                  <DebouncedTextarea
                    id='descripcion'
                    placeholder='Resumen de los servicios...'
                    value={datosFactura.descripcion}
                    onChange={(e) => setDatosFactura({...datosFactura, descripcion: e.target.value})}
                    rows={3}
                    className='resize-none'
                  />
                </div>

                {/* Número de Factura */}
                <div className='space-y-2'>
                  <Label htmlFor='numero_factura'>Número de Factura *</Label>
                  <DebouncedInput
                    id='numero_factura'
                    placeholder='Ej: 12345'
                    value={datosFactura.numero_factura}
                    onChange={(e) => setDatosFactura({...datosFactura, numero_factura: e.target.value})}
                  />
                </div>

                {/* Fechas en grid */}
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='fecha_emision'>Fecha de Emisión *</Label>
                    <Input
                      id='fecha_emision'
                      type='date'
                      value={datosFactura.fecha_emision}
                      onChange={(e) => setDatosFactura({...datosFactura, fecha_emision: e.target.value})}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='fecha_vencimiento'>Fecha de Vencimiento *</Label>
                    <Input
                      id='fecha_vencimiento'
                      type='date'
                      value={datosFactura.fecha_vencimiento}
                      onChange={(e) => handleFechaVencimientoChange(e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Monto */}
                <div className='space-y-2'>
                  <Label htmlFor='monto_facturado'>Monto de la Factura (CLP) *</Label>
                  <DebouncedInput
                    id='monto_facturado'
                    type='number'
                    placeholder='Ej: 1500000'
                    value={datosFactura.monto_facturado}
                    onChange={(e) => setDatosFactura({...datosFactura, monto_facturado: e.target.value})}
                  />
                  {datosFactura.monto_facturado && valorUF && (
                    <p className='text-xs text-slate-500'>
                      ≈ {(parseFloat(datosFactura.monto_facturado) / valorUF).toFixed(2)} UF (al valor del día)
                    </p>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant='outline' onClick={() => setShowFacturarModal(false)} disabled={facturando}>
                  Cancelar
                </Button>
                <Button onClick={handleFacturar} disabled={facturando} className='bg-blue-600 hover:bg-blue-700'>
                  {facturando ? (
                    <>
                      <div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2'></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className='w-4 h-4 mr-2' />
                      Finalizar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal Registrar Pago */}
          <Dialog open={showRegistrarPago} onOpenChange={setShowRegistrarPago}>
            <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <DollarSign className='w-5 h-5 text-green-600' />
                  Registrar Pago de Factura
                </DialogTitle>
                <DialogDescription>
                  {facturaSeleccionadaPago && (
                    <span>
                      Factura #{facturaSeleccionadaPago.numero_factura} - {facturaSeleccionadaPago.cliente?.company || facturaSeleccionadaPago.cliente?.name}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              {facturaSeleccionadaPago && (
                <div className='space-y-6'>
                  {/* Info de la factura */}
                  <Card className='p-4 bg-slate-50 border-slate-200'>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <p className='text-slate-500'>Monto Total</p>
                        <p className='font-bold text-lg text-slate-900'>
                          ${new Intl.NumberFormat('es-CL').format(facturaSeleccionadaPago.total_amount_clp || facturaSeleccionadaPago.monto_clp || 0)}
                        </p>
                        <p className='text-xs text-slate-400'>
                          {(facturaSeleccionadaPago.original_amount_uf || facturaSeleccionadaPago.monto_uf)?.toFixed(2)} UF ref.
                        </p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Monto Pendiente</p>
                        <p className='font-bold text-lg text-amber-600'>
                          ${new Intl.NumberFormat('es-CL').format(facturaSeleccionadaPago.pending_amount_clp || facturaSeleccionadaPago.total_amount_clp || 0)}
                        </p>
                      </div>
                      {(facturaSeleccionadaPago.paid_amount_clp || 0) > 0 && (
                        <>
                          <div>
                            <p className='text-slate-500'>Pagado</p>
                            <p className='font-bold text-green-600'>
                              ${new Intl.NumberFormat('es-CL').format(facturaSeleccionadaPago.paid_amount_clp || 0)}
                            </p>
                          </div>
                          <div>
                            <p className='text-slate-500'>Estado</p>
                            <Badge 
                              variant='outline'
                              className={
                                facturaSeleccionadaPago.payment_status === 'paid'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : facturaSeleccionadaPago.payment_status === 'partially_paid'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                              }
                            >
                              {facturaSeleccionadaPago.payment_status === 'paid' 
                                ? 'Pagada' 
                                : facturaSeleccionadaPago.payment_status === 'partially_paid' 
                                  ? 'Pago Parcial' 
                                  : 'Pendiente'}
                            </Badge>
                          </div>
                        </>
                      )}
                    </div>
                  </Card>

                  {/* Historial de pagos */}
                  {historialPagos.length > 0 && (
                    <div>
                      <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                        <History className='w-4 h-4' />
                        Historial de Pagos ({historialPagos.length})
                      </h4>
                      <div className='border rounded-lg overflow-hidden'>
                        <table className='w-full text-sm'>
                          <thead className='bg-slate-50'>
                            <tr>
                              <th className='text-left p-2 text-slate-600'>Fecha</th>
                              <th className='text-left p-2 text-slate-600'>Método</th>
                              <th className='text-right p-2 text-slate-600'>Monto</th>
                              <th className='text-center p-2 text-slate-600'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialPagos.map((pago) => (
                              <tr key={pago.id} className='border-t hover:bg-slate-50'>
                                <td className='p-2'>
                                  <p className='font-medium'>{new Date(pago.payment_date).toLocaleDateString('es-CL')}</p>
                                  {pago.reference && (
                                    <p className='text-xs text-slate-500'>Ref: {pago.reference}</p>
                                  )}
                                </td>
                                <td className='p-2 capitalize'>
                                  {metodosPago.find(m => m.value === pago.payment_method)?.label || pago.payment_method}
                                </td>
                                <td className='p-2 text-right font-bold text-green-600'>
                                  ${new Intl.NumberFormat('es-CL').format(pago.amount_clp || pago.amount || 0)}
                                </td>
                                <td className='p-2 text-center'>
                                  <Button
                                    size='sm'
                                    variant='ghost'
                                    className='h-6 text-xs text-red-500 hover:text-red-700 hover:bg-red-50'
                                    onClick={() => handleEliminarPago(pago.id, facturaSeleccionadaPago.id)}
                                    data-testid={`eliminar-pago-${pago.id}`}
                                  >
                                    <Trash2 className='w-3 h-3' />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Formulario de nuevo pago */}
                  {(facturaSeleccionadaPago.pending_amount_clp ?? facturaSeleccionadaPago.total_amount_clp ?? 0) > 100 && (
                    <div className='border-t pt-4'>
                      <h4 className='text-sm font-semibold text-slate-700 mb-3'>Nuevo Pago</h4>
                      <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                          <Label htmlFor='payment_amount'>Monto (CLP) *</Label>
                          <DebouncedInput
                            id='payment_amount'
                            type='number'
                            step='1'
                            min='1'
                            max={facturaSeleccionadaPago.pending_amount_clp ?? facturaSeleccionadaPago.total_amount_clp}
                            placeholder='Ej: 500000'
                            value={nuevoPago.amount_clp}
                            onChange={(e) => setNuevoPago({...nuevoPago, amount_clp: e.target.value})}
                            data-testid='payment-amount-input'
                          />
                          <Button
                            type='button'
                            variant='link'
                            className='h-auto p-0 text-xs text-blue-600'
                            onClick={() => setNuevoPago({
                              ...nuevoPago, 
                              amount_clp: (facturaSeleccionadaPago.pending_amount_clp ?? facturaSeleccionadaPago.total_amount_clp)?.toString()
                            })}
                          >
                            Pagar total pendiente
                          </Button>
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='payment_date'>Fecha de Pago *</Label>
                          <Input
                            id='payment_date'
                            type='date'
                            value={nuevoPago.payment_date}
                            onChange={(e) => setNuevoPago({...nuevoPago, payment_date: e.target.value})}
                            data-testid='payment-date-input'
                          />
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='payment_method'>Método de Pago</Label>
                          <Select
                            value={nuevoPago.payment_method}
                            onValueChange={(value) => setNuevoPago({...nuevoPago, payment_method: value})}
                          >
                            <SelectTrigger data-testid='payment-method-select'>
                              <SelectValue placeholder='Seleccionar método' />
                            </SelectTrigger>
                            <SelectContent>
                              {metodosPago.map((metodo) => (
                                <SelectItem key={metodo.value} value={metodo.value}>
                                  {metodo.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className='space-y-2'>
                          <Label htmlFor='payment_reference'>Referencia / N° Operación</Label>
                          <DebouncedInput
                            id='payment_reference'
                            placeholder='Ej: TRF-123456'
                            value={nuevoPago.reference}
                            onChange={(e) => setNuevoPago({...nuevoPago, reference: e.target.value})}
                            data-testid='payment-reference-input'
                          />
                        </div>
                        <div className='col-span-2 space-y-2'>
                          <Label htmlFor='payment_notes'>Notas (opcional)</Label>
                          <DebouncedInput
                            id='payment_notes'
                            placeholder='Observaciones adicionales...'
                            value={nuevoPago.notes}
                            onChange={(e) => setNuevoPago({...nuevoPago, notes: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowRegistrarPago(false)}>
                  Cerrar
                </Button>
                {(facturaSeleccionadaPago?.pending_amount_clp ?? facturaSeleccionadaPago?.total_amount_clp ?? 0) > 100 && (
                  <Button 
                    onClick={handleRegistrarPago} 
                    className='bg-green-600 hover:bg-green-700'
                    data-testid='confirmar-pago-btn'
                  >
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Registrar Pago
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal Conciliar con Factura */}
          <Dialog open={showConciliarFactura} onOpenChange={setShowConciliarFactura}>
            <DialogContent className='sm:max-w-[800px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Link className='w-5 h-5 text-blue-600' />
                  Conciliar Abono con Factura
                </DialogTitle>
                <DialogDescription>
                  Vincule este movimiento bancario con una factura en cobro para registrar el pago automáticamente.
                </DialogDescription>
              </DialogHeader>
              
              {movimientoAConciliar && (
                <div className='space-y-5'>
                  {/* Info del movimiento bancario */}
                  <Card className='p-4 bg-green-50 border-green-200'>
                    <h4 className='text-sm font-semibold text-green-800 mb-2 flex items-center gap-2'>
                      <ArrowUpCircle className='w-4 h-4' />
                      Movimiento Bancario (Abono)
                    </h4>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <p className='text-slate-500'>Fecha</p>
                        <p className='font-medium'>{new Date(movimientoAConciliar.transaction_date + 'T00:00:00').toLocaleDateString('es-CL')}</p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Monto</p>
                        <p className='font-bold text-green-700 text-lg'>
                          ${new Intl.NumberFormat('es-CL').format(movimientoAConciliar.amount)}
                        </p>
                        <p className='text-xs text-green-600'>
                          ≈ {(movimientoAConciliar.amount / ufValueConciliacion).toFixed(2)} UF
                        </p>
                      </div>
                      <div className='col-span-2'>
                        <p className='text-slate-500'>Descripción</p>
                        <p className='font-medium text-slate-900'>{movimientoAConciliar.description_raw}</p>
                      </div>
                    </div>
                  </Card>

                  {/* Lista de sugerencias */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2'>
                      <Receipt className='w-4 h-4' />
                      Facturas Disponibles para Conciliar
                      {sugerenciasConciliacion.length > 0 && (
                        <Badge variant='outline' className='ml-2'>{sugerenciasConciliacion.length}</Badge>
                      )}
                    </h4>
                    
                    {loadingSugerencias ? (
                      <Card className='p-8 text-center'>
                        <div className='animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2'></div>
                        <p className='text-slate-600'>Buscando facturas compatibles...</p>
                      </Card>
                    ) : sugerenciasConciliacion.length === 0 ? (
                      <Card className='p-8 text-center border-slate-200'>
                        <Receipt className='w-12 h-12 text-slate-300 mx-auto mb-3' />
                        <p className='text-slate-600'>No hay facturas pendientes disponibles para conciliar</p>
                        <p className='text-sm text-slate-400'>Las facturas en cobro aparecerán aquí</p>
                      </Card>
                    ) : (
                      <div className='max-h-[280px] overflow-y-auto border rounded-lg'>
                        <table className='w-full text-sm'>
                          <thead className='bg-slate-50 sticky top-0'>
                            <tr>
                              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Factura</th>
                              <th className='text-left p-2 font-semibold text-slate-600 border-b'>Cliente</th>
                              <th className='text-right p-2 font-semibold text-slate-600 border-b'>Pendiente</th>
                              <th className='text-center p-2 font-semibold text-slate-600 border-b'>Match</th>
                              <th className='text-center p-2 font-semibold text-slate-600 border-b'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sugerenciasConciliacion.map((sug) => (
                              <tr 
                                key={sug.cobranza_id}
                                className={`border-b hover:bg-slate-50 transition-colors cursor-pointer
                                  ${facturaSeleccionadaConciliar?.cobranza_id === sug.cobranza_id ? 'bg-blue-50' : ''}
                                `}
                                onClick={() => handleSeleccionarFacturaConciliar(sug)}
                              >
                                <td className='p-2'>
                                  <p className='font-bold text-slate-900'>#{sug.numero_factura}</p>
                                  <p className='text-xs text-slate-500'>{sug.proposal_folio}</p>
                                </td>
                                <td className='p-2'>
                                  <p className='font-medium text-slate-900 truncate max-w-[150px]'>{sug.cliente_name}</p>
                                  <p className='text-xs text-slate-500'>{sug.cliente_rut}</p>
                                </td>
                                <td className='p-2 text-right'>
                                  <p className='font-bold text-amber-600'>{sug.pending_amount_uf.toFixed(2)} UF</p>
                                  <p className='text-xs text-slate-500'>
                                    ${new Intl.NumberFormat('es-CL').format(sug.pending_amount_clp)}
                                  </p>
                                </td>
                                <td className='p-2 text-center'>
                                  <div className='flex flex-col items-center'>
                                    <Badge 
                                      variant='outline'
                                      className={`text-xs
                                        ${sug.match_score >= 70 ? 'bg-green-50 text-green-700 border-green-200' : 
                                          sug.match_score >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 
                                          'bg-slate-50 text-slate-600 border-slate-200'}
                                      `}
                                    >
                                      {sug.match_score}%
                                    </Badge>
                                    {sug.match_reasons.length > 0 && (
                                      <p className='text-xs text-slate-500 mt-1 truncate max-w-[100px]' title={sug.match_reasons.join(', ')}>
                                        {sug.match_reasons[0]}
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td className='p-2 text-center'>
                                  <Button
                                    size='sm'
                                    variant={facturaSeleccionadaConciliar?.cobranza_id === sug.cobranza_id ? 'default' : 'outline'}
                                    className='h-7 text-xs'
                                    onClick={(e) => { e.stopPropagation(); handleSeleccionarFacturaConciliar(sug); }}
                                  >
                                    {facturaSeleccionadaConciliar?.cobranza_id === sug.cobranza_id ? 'Seleccionada' : 'Seleccionar'}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Formulario de conciliación */}
                  {facturaSeleccionadaConciliar && (
                    <Card className='p-4 bg-blue-50 border-blue-200'>
                      <h4 className='text-sm font-semibold text-blue-800 mb-3'>Confirmar Conciliación</h4>
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <p className='text-xs text-slate-500 mb-1'>Factura seleccionada</p>
                          <p className='font-bold text-slate-900'>
                            #{facturaSeleccionadaConciliar.numero_factura} - {facturaSeleccionadaConciliar.cliente_name}
                          </p>
                          <p className='text-xs text-slate-600'>
                            Pendiente: {facturaSeleccionadaConciliar.pending_amount_uf.toFixed(2)} UF
                          </p>
                        </div>
                        <div>
                          <Label htmlFor='monto_conciliar' className='text-xs'>Monto a Conciliar (UF)</Label>
                          <Input
                            id='monto_conciliar'
                            type='number'
                            step='0.01'
                            min='0.01'
                            max={facturaSeleccionadaConciliar.pending_amount_uf}
                            value={montoConciliar}
                            onChange={(e) => setMontoConciliar(e.target.value)}
                            className='h-9'
                            data-testid='monto-conciliar-input'
                          />
                          <Button
                            type='button'
                            variant='link'
                            className='h-auto p-0 text-xs text-blue-600'
                            onClick={() => setMontoConciliar(facturaSeleccionadaConciliar.pending_amount_uf.toFixed(2))}
                          >
                            Usar monto pendiente completo
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              )}
              
              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowConciliarFactura(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleConfirmarConciliacion}
                  disabled={!facturaSeleccionadaConciliar || !montoConciliar}
                  className='bg-blue-600 hover:bg-blue-700'
                  data-testid='confirmar-conciliacion-btn'
                >
                  <Link className='w-4 h-4 mr-2' />
                  Confirmar Conciliación
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal Nota de Crédito */}
          <Dialog open={showNotaCredito} onOpenChange={setShowNotaCredito}>
            <DialogContent className='sm:max-w-[700px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <ArrowDownCircle className='w-5 h-5 text-blue-600' />
                  Emitir Nota de Crédito
                </DialogTitle>
                <DialogDescription>
                  Emita una nota de crédito para reducir el saldo pendiente de la factura.
                </DialogDescription>
              </DialogHeader>
              
              {facturaNotaCredito && (
                <div className='space-y-5'>
                  {/* Info de la factura */}
                  <Card className='p-4 bg-slate-50 border-slate-200'>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <p className='text-slate-500'>Cliente</p>
                        <p className='font-bold text-slate-900'>{facturaNotaCredito.cliente?.company || facturaNotaCredito.cliente?.name}</p>
                        <p className='text-xs text-slate-500'>RUT: {formatRutSinPuntos(facturaNotaCredito.facturacion?.rut || facturaNotaCredito.cliente?.rut)}</p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Factura N°</p>
                        <p className='font-bold text-slate-900'>{facturaNotaCredito.numero_factura}</p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Monto Total Factura</p>
                        <p className='font-bold text-lg text-slate-900'>
                          ${new Intl.NumberFormat('es-CL').format(facturaNotaCredito.total_amount_clp || facturaNotaCredito.monto_clp || 0)}
                        </p>
                        <p className='text-xs text-slate-400'>
                          {(facturaNotaCredito.original_amount_uf || facturaNotaCredito.monto_uf)?.toFixed(2)} UF ref.
                        </p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Pagado</p>
                        <p className='font-bold text-green-600'>
                          ${new Intl.NumberFormat('es-CL').format(facturaNotaCredito.paid_amount_clp || 0)}
                        </p>
                      </div>
                      <div className='col-span-2'>
                        <p className='text-slate-500'>Saldo Pendiente Actual</p>
                        <p className='font-bold text-xl text-amber-600'>
                          ${new Intl.NumberFormat('es-CL').format(facturaNotaCredito.pending_amount_clp || facturaNotaCredito.total_amount_clp || 0)}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Formulario */}
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='nc_number'>Número de Nota de Crédito *</Label>
                      <DebouncedInput
                        id='nc_number'
                        placeholder='Ej: NC-001'
                        value={notaCreditoForm.document_number}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, document_number: value}));
                        }}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nc_date'>Fecha de Emisión *</Label>
                      <Input
                        id='nc_date'
                        type='date'
                        value={notaCreditoForm.issue_date}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, issue_date: value}));
                        }}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nc_amount'>Monto (CLP) *</Label>
                      <DebouncedInput
                        id='nc_amount'
                        type='number'
                        min='1'
                        max={facturaNotaCredito.pending_amount_clp || facturaNotaCredito.total_amount_clp}
                        placeholder='Ej: 100000'
                        value={notaCreditoForm.amount_clp}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, amount_clp: value}));
                        }}
                      />
                      <p className='text-xs text-slate-500'>
                        Máximo: ${(facturaNotaCredito.pending_amount_clp || facturaNotaCredito.total_amount_clp || 0).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nc_reference'>Referencia</Label>
                      <DebouncedInput
                        id='nc_reference'
                        placeholder='Ej: Descuento comercial'
                        value={notaCreditoForm.reference_text}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, reference_text: value}));
                        }}
                      />
                    </div>
                    <div className='col-span-2 space-y-2'>
                      <Label htmlFor='nc_reason'>Motivo *</Label>
                      <DebouncedInput
                        id='nc_reason'
                        placeholder='Ej: Descuento por pronto pago, ajuste de precio, etc.'
                        value={notaCreditoForm.reason}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, reason: value}));
                        }}
                      />
                    </div>
                    <div className='col-span-2 space-y-2'>
                      <Label htmlFor='nc_notes'>Observaciones</Label>
                      <DebouncedTextarea
                        id='nc_notes'
                        placeholder='Observaciones adicionales...'
                        value={notaCreditoForm.notes}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaCreditoForm(prev => ({...prev, notes: value}));
                        }}
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Historial de documentos de ajuste */}
                  {documentosAjuste.length > 0 && (
                    <div className='border-t pt-4'>
                      <h4 className='text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2'>
                        <History className='w-4 h-4' />
                        Notas Asociadas ({documentosAjuste.length})
                      </h4>
                      <div className='overflow-x-auto'>
                        <table className='w-full text-xs'>
                          <thead>
                            <tr className='bg-slate-100'>
                              <th className='text-left p-2 font-semibold'>Tipo</th>
                              <th className='text-left p-2 font-semibold'>Número</th>
                              <th className='text-left p-2 font-semibold'>Fecha</th>
                              <th className='text-right p-2 font-semibold'>Monto</th>
                              <th className='text-left p-2 font-semibold'>Motivo</th>
                              <th className='text-center p-2 font-semibold'>Estado</th>
                              <th className='text-center p-2 font-semibold'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentosAjuste.map((doc) => (
                              <tr key={doc.id} className={`border-b ${doc.status === 'voided' ? 'bg-red-50 opacity-60' : ''}`}>
                                <td className='p-2'>
                                  <Badge 
                                    variant='outline' 
                                    className={doc.document_type === 'credit_note' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
                                  >
                                    {doc.document_type === 'credit_note' ? 'NC' : 'ND'}
                                  </Badge>
                                </td>
                                <td className='p-2 font-mono'>{doc.document_number}</td>
                                <td className='p-2'>{new Date(doc.issue_date).toLocaleDateString('es-CL')}</td>
                                <td className='p-2 text-right font-mono font-bold'>
                                  ${new Intl.NumberFormat('es-CL').format(doc.amount_clp)}
                                </td>
                                <td className='p-2 truncate max-w-[150px]' title={doc.reason}>{doc.reason}</td>
                                <td className='p-2 text-center'>
                                  <Badge 
                                    variant='outline' 
                                    className={doc.status === 'issued' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}
                                  >
                                    {doc.status === 'issued' ? 'Vigente' : 'Anulada'}
                                  </Badge>
                                </td>
                                <td className='p-2 text-center'>
                                  {doc.status === 'issued' && (
                                    <Button
                                      size='sm'
                                      variant='ghost'
                                      className='h-6 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50'
                                      onClick={() => handleAnularDocumento(doc.id, doc.document_type)}
                                    >
                                      <X className='w-3 h-3 mr-1' />
                                      Anular
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowNotaCredito(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleEmitirNotaCredito} 
                  className='bg-blue-600 hover:bg-blue-700'
                >
                  <ArrowDownCircle className='w-4 h-4 mr-2' />
                  Emitir Nota de Crédito
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal Nota de Débito */}
          <Dialog open={showNotaDebito} onOpenChange={setShowNotaDebito}>
            <DialogContent className='sm:max-w-[700px] max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <ArrowUpCircle className='w-5 h-5 text-amber-600' />
                  Emitir Nota de Débito
                </DialogTitle>
                <DialogDescription>
                  Emita una nota de débito para aumentar el saldo pendiente de la factura.
                </DialogDescription>
              </DialogHeader>
              
              {facturaNotaDebito && (
                <div className='space-y-5'>
                  {/* Info de la factura */}
                  <Card className='p-4 bg-slate-50 border-slate-200'>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div>
                        <p className='text-slate-500'>Cliente</p>
                        <p className='font-bold text-slate-900'>{facturaNotaDebito.cliente?.company || facturaNotaDebito.cliente?.name}</p>
                        <p className='text-xs text-slate-500'>RUT: {formatRutSinPuntos(facturaNotaDebito.facturacion?.rut || facturaNotaDebito.cliente?.rut)}</p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Factura N°</p>
                        <p className='font-bold text-slate-900'>{facturaNotaDebito.numero_factura}</p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Monto Total Factura</p>
                        <p className='font-bold text-lg text-slate-900'>
                          ${new Intl.NumberFormat('es-CL').format(facturaNotaDebito.total_amount_clp || facturaNotaDebito.monto_clp || 0)}
                        </p>
                        <p className='text-xs text-slate-400'>
                          {(facturaNotaDebito.original_amount_uf || facturaNotaDebito.monto_uf)?.toFixed(2)} UF ref.
                        </p>
                      </div>
                      <div>
                        <p className='text-slate-500'>Pagado</p>
                        <p className='font-bold text-green-600'>
                          ${new Intl.NumberFormat('es-CL').format(facturaNotaDebito.paid_amount_clp || 0)}
                        </p>
                      </div>
                      <div className='col-span-2'>
                        <p className='text-slate-500'>Saldo Pendiente Actual</p>
                        <p className='font-bold text-xl text-amber-600'>
                          ${(facturaNotaDebito.pending_amount_clp || facturaNotaDebito.total_amount_clp || 0).toLocaleString('es-CL')}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Formulario */}
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='nd_number'>Número de Nota de Débito *</Label>
                      <DebouncedInput
                        id='nd_number'
                        placeholder='Ej: ND-001'
                        value={notaDebitoForm.document_number}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, document_number: value}));
                        }}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nd_date'>Fecha de Emisión *</Label>
                      <Input
                        id='nd_date'
                        type='date'
                        value={notaDebitoForm.issue_date}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, issue_date: value}));
                        }}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nd_amount'>Monto (CLP) *</Label>
                      <DebouncedInput
                        id='nd_amount'
                        type='number'
                        min='1'
                        placeholder='Ej: 50000'
                        value={notaDebitoForm.amount_clp}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, amount_clp: value}));
                        }}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='nd_reference'>Referencia</Label>
                      <DebouncedInput
                        id='nd_reference'
                        placeholder='Ej: Interés por mora'
                        value={notaDebitoForm.reference_text}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, reference_text: value}));
                        }}
                      />
                    </div>
                    <div className='col-span-2 space-y-2'>
                      <Label htmlFor='nd_reason'>Motivo *</Label>
                      <DebouncedInput
                        id='nd_reason'
                        placeholder='Ej: Interés por atraso en pago, ajuste de precio, etc.'
                        value={notaDebitoForm.reason}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, reason: value}));
                        }}
                      />
                    </div>
                    <div className='col-span-2 space-y-2'>
                      <Label htmlFor='nd_notes'>Observaciones</Label>
                      <DebouncedTextarea
                        id='nd_notes'
                        placeholder='Observaciones adicionales...'
                        value={notaDebitoForm.notes}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotaDebitoForm(prev => ({...prev, notes: value}));
                        }}
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Historial de documentos de ajuste */}
                  {documentosAjuste.length > 0 && (
                    <div className='border-t pt-4'>
                      <h4 className='text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2'>
                        <History className='w-4 h-4' />
                        Notas Asociadas ({documentosAjuste.length})
                      </h4>
                      <div className='overflow-x-auto'>
                        <table className='w-full text-xs'>
                          <thead>
                            <tr className='bg-slate-100'>
                              <th className='text-left p-2 font-semibold'>Tipo</th>
                              <th className='text-left p-2 font-semibold'>Número</th>
                              <th className='text-left p-2 font-semibold'>Fecha</th>
                              <th className='text-right p-2 font-semibold'>Monto</th>
                              <th className='text-left p-2 font-semibold'>Motivo</th>
                              <th className='text-center p-2 font-semibold'>Estado</th>
                              <th className='text-center p-2 font-semibold'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentosAjuste.map((doc) => (
                              <tr key={doc.id} className={`border-b ${doc.status === 'voided' ? 'bg-red-50 opacity-60' : ''}`}>
                                <td className='p-2'>
                                  <Badge 
                                    variant='outline' 
                                    className={doc.document_type === 'credit_note' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
                                  >
                                    {doc.document_type === 'credit_note' ? 'NC' : 'ND'}
                                  </Badge>
                                </td>
                                <td className='p-2 font-mono'>{doc.document_number}</td>
                                <td className='p-2'>{new Date(doc.issue_date).toLocaleDateString('es-CL')}</td>
                                <td className='p-2 text-right font-mono font-bold'>
                                  ${new Intl.NumberFormat('es-CL').format(doc.amount_clp)}
                                </td>
                                <td className='p-2 truncate max-w-[150px]' title={doc.reason}>{doc.reason}</td>
                                <td className='p-2 text-center'>
                                  <Badge 
                                    variant='outline' 
                                    className={doc.status === 'issued' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}
                                  >
                                    {doc.status === 'issued' ? 'Vigente' : 'Anulada'}
                                  </Badge>
                                </td>
                                <td className='p-2 text-center'>
                                  {doc.status === 'issued' && (
                                    <Button
                                      size='sm'
                                      variant='ghost'
                                      className='h-6 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50'
                                      onClick={() => handleAnularDocumento(doc.id, doc.document_type)}
                                    >
                                      <X className='w-3 h-3 mr-1' />
                                      Anular
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowNotaDebito(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleEmitirNotaDebito} 
                  className='bg-amber-600 hover:bg-amber-700'
                >
                  <ArrowUpCircle className='w-4 h-4 mr-2' />
                  Emitir Nota de Débito
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Historial Financiero Completo */}
          <Dialog open={showHistorialFinanciero} onOpenChange={setShowHistorialFinanciero}>
            <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <History className='w-5 h-5 text-slate-600' />
                  Historial Financiero
                  {historialFinancieroData && (
                    <span className='text-sm font-normal text-slate-500'>
                      - Factura #{historialFinancieroData.numero_factura}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              {loadingHistorialFinanciero ? (
                <div className='p-12 text-center'>
                  <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2'></div>
                  <p className='text-slate-600'>Cargando historial...</p>
                </div>
              ) : historialFinancieroData ? (
                <div className='space-y-6'>
                  {/* Resumen de la Factura */}
                  <Card className='p-4 bg-slate-50 border-slate-200'>
                    <h4 className='text-sm font-semibold text-slate-700 mb-3'>Resumen de Factura</h4>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                      <div>
                        <p className='text-xs text-slate-500'>Monto Factura</p>
                        <p className='font-bold text-slate-900'>
                          ${new Intl.NumberFormat('es-CL').format(historialFinancieroData.total_amount_clp || 0)}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-slate-500'>Pagado Real</p>
                        <p className='font-bold text-green-700'>
                          ${new Intl.NumberFormat('es-CL').format(historialFinancieroData.paid_amount_clp || 0)}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-slate-500'>Notas de Crédito</p>
                        <p className='font-bold text-blue-700'>
                          ${new Intl.NumberFormat('es-CL').format(historialFinancieroData.credited_amount_clp || 0)}
                        </p>
                      </div>
                      <div>
                        <p className='text-xs text-slate-500'>Notas de Débito</p>
                        <p className='font-bold text-amber-700'>
                          ${new Intl.NumberFormat('es-CL').format(historialFinancieroData.debited_amount_clp || 0)}
                        </p>
                      </div>
                    </div>
                    <div className='mt-3 pt-3 border-t border-slate-200 flex items-center justify-between'>
                      <div>
                        <p className='text-xs text-slate-500'>Saldo Pendiente</p>
                        <p className='font-bold text-slate-900'>
                          ${new Intl.NumberFormat('es-CL').format(historialFinancieroData.pending_amount_clp || 0)}
                        </p>
                      </div>
                      <div>
                        {historialFinancieroData.closing_type === 'paid_full' ? (
                          <Badge className='bg-green-100 text-green-700 border-green-200'>
                            Cobrada 100%
                          </Badge>
                        ) : historialFinancieroData.closing_type === 'mixed' ? (
                          <Badge className='bg-blue-100 text-blue-700 border-blue-200'>
                            Cerrada: Pago + NC
                          </Badge>
                        ) : historialFinancieroData.closing_type === 'credit_note_only' ? (
                          <Badge className='bg-purple-100 text-purple-700 border-purple-200'>
                            Cerrada solo con NC
                          </Badge>
                        ) : historialFinancieroData.pending_amount_clp === 0 ? (
                          <Badge className='bg-slate-100 text-slate-700 border-slate-200'>
                            Cerrada
                          </Badge>
                        ) : (
                          <Badge className='bg-amber-100 text-amber-700 border-amber-200'>
                            Pendiente
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>

                  {/* Sección: Pagos Reales */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                      <DollarSign className='w-4 h-4 text-green-600' />
                      Pagos Recibidos ({historialFinancieroData.total_pagos || 0})
                    </h4>
                    {(historialFinancieroData.pagos?.length || 0) === 0 ? (
                      <p className='text-sm text-slate-500 italic'>No hay pagos registrados</p>
                    ) : (
                      <div className='border border-slate-200 rounded-lg overflow-hidden'>
                        <table className='w-full text-sm'>
                          <thead className='bg-green-50'>
                            <tr>
                              <th className='text-left p-2 font-medium text-slate-600'>Fecha</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Método</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Referencia</th>
                              <th className='text-right p-2 font-medium text-slate-600'>Monto</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Origen</th>
                              <th className='text-center p-2 font-medium text-slate-600'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialFinancieroData.pagos.map((pago, idx) => (
                              <tr key={idx} className='border-t border-slate-100'>
                                <td className='p-2'>
                                  {pago.payment_date ? new Date(pago.payment_date).toLocaleDateString('es-CL') : 'N/A'}
                                </td>
                                <td className='p-2 capitalize'>{pago.payment_method || '-'}</td>
                                <td className='p-2 text-slate-600'>{pago.reference || '-'}</td>
                                <td className='p-2 text-right font-semibold text-green-700'>
                                  ${new Intl.NumberFormat('es-CL').format(pago.amount_clp || 0)}
                                </td>
                                <td className='p-2'>
                                  {pago.source === 'bank_reconciliation' ? (
                                    <Badge variant='outline' className='text-xs bg-blue-50'>Conciliación</Badge>
                                  ) : (
                                    <Badge variant='outline' className='text-xs'>Manual</Badge>
                                  )}
                                </td>
                                <td className='p-2 text-center'>
                                  {pago.source !== 'bank_reconciliation' && (
                                    <Button
                                      size='sm'
                                      variant='ghost'
                                      className='h-7 text-xs text-red-600 hover:text-red-800 hover:bg-red-50'
                                      onClick={() => handleEliminarPago(pago.id, historialFinancieroData.cobranza_id)}
                                      data-testid={`eliminar-pago-btn-${pago.id}`}
                                    >
                                      <Trash2 className='w-3 h-3 mr-1' />
                                      Eliminar
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Sección: Notas de Crédito */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                      <ArrowDownCircle className='w-4 h-4 text-blue-600' />
                      Notas de Crédito ({historialFinancieroData.total_notas_credito || 0})
                    </h4>
                    {(historialFinancieroData.notas_credito?.length || 0) === 0 ? (
                      <p className='text-sm text-slate-500 italic'>No hay notas de crédito</p>
                    ) : (
                      <div className='border border-slate-200 rounded-lg overflow-hidden'>
                        <table className='w-full text-sm'>
                          <thead className='bg-blue-50'>
                            <tr>
                              <th className='text-left p-2 font-medium text-slate-600'>N° NC</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Fecha</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Motivo</th>
                              <th className='text-right p-2 font-medium text-slate-600'>Monto</th>
                              <th className='text-center p-2 font-medium text-slate-600'>Estado</th>
                              <th className='text-center p-2 font-medium text-slate-600'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialFinancieroData.notas_credito.map((nc, idx) => (
                              <tr key={idx} className='border-t border-slate-100'>
                                <td className='p-2 font-medium'>{nc.document_number}</td>
                                <td className='p-2'>
                                  {nc.issue_date ? new Date(nc.issue_date).toLocaleDateString('es-CL') : 'N/A'}
                                </td>
                                <td className='p-2 text-slate-600 max-w-[200px] truncate'>{nc.reason || '-'}</td>
                                <td className='p-2 text-right font-semibold text-blue-700'>
                                  ${new Intl.NumberFormat('es-CL').format(nc.amount_clp || 0)}
                                </td>
                                <td className='p-2 text-center'>
                                  {nc.status === 'issued' ? (
                                    <Badge className='bg-green-100 text-green-700'>Emitida</Badge>
                                  ) : (
                                    <Badge className='bg-red-100 text-red-700'>Anulada</Badge>
                                  )}
                                </td>
                                <td className='p-2 text-center'>
                                  {nc.status === 'issued' && (
                                    <Button
                                      size='sm'
                                      variant='ghost'
                                      className='h-7 text-xs text-red-600 hover:text-red-800 hover:bg-red-50'
                                      onClick={() => handleAnularNota(nc.id, 'credit_note', historialFinancieroData.cobranza_id)}
                                      data-testid={`anular-nc-btn-${nc.id}`}
                                    >
                                      <XCircle className='w-3 h-3 mr-1' />
                                      Anular
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Sección: Notas de Débito */}
                  {(historialFinancieroData.notas_debito?.length || 0) > 0 && (
                    <div>
                      <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                        <ArrowUpCircle className='w-4 h-4 text-amber-600' />
                        Notas de Débito ({historialFinancieroData.total_notas_debito || 0})
                      </h4>
                      <div className='border border-slate-200 rounded-lg overflow-hidden'>
                        <table className='w-full text-sm'>
                          <thead className='bg-amber-50'>
                            <tr>
                              <th className='text-left p-2 font-medium text-slate-600'>N° ND</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Fecha</th>
                              <th className='text-left p-2 font-medium text-slate-600'>Motivo</th>
                              <th className='text-right p-2 font-medium text-slate-600'>Monto</th>
                              <th className='text-center p-2 font-medium text-slate-600'>Estado</th>
                              <th className='text-center p-2 font-medium text-slate-600'>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialFinancieroData.notas_debito.map((nd, idx) => (
                              <tr key={idx} className='border-t border-slate-100'>
                                <td className='p-2 font-medium'>{nd.document_number}</td>
                                <td className='p-2'>
                                  {nd.issue_date ? new Date(nd.issue_date).toLocaleDateString('es-CL') : 'N/A'}
                                </td>
                                <td className='p-2 text-slate-600 max-w-[200px] truncate'>{nd.reason || '-'}</td>
                                <td className='p-2 text-right font-semibold text-amber-700'>
                                  +${new Intl.NumberFormat('es-CL').format(nd.amount_clp || 0)}
                                </td>
                                <td className='p-2 text-center'>
                                  {nd.status === 'issued' ? (
                                    <Badge className='bg-green-100 text-green-700'>Emitida</Badge>
                                  ) : (
                                    <Badge className='bg-red-100 text-red-700'>Anulada</Badge>
                                  )}
                                </td>
                                <td className='p-2 text-center'>
                                  {nd.status === 'issued' && (
                                    <Button
                                      size='sm'
                                      variant='ghost'
                                      className='h-7 text-xs text-red-600 hover:text-red-800 hover:bg-red-50'
                                      onClick={() => handleAnularNota(nd.id, 'debit_note', historialFinancieroData.cobranza_id)}
                                      data-testid={`anular-nd-btn-${nd.id}`}
                                    >
                                      <XCircle className='w-3 h-3 mr-1' />
                                      Anular
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className='p-12 text-center'>
                  <AlertTriangle className='w-12 h-12 text-slate-300 mx-auto mb-3' />
                  <p className='text-slate-600'>No se pudo cargar el historial</p>
                </div>
              )}

              <DialogFooter>
                <Button variant='outline' onClick={() => setShowHistorialFinanciero(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Nueva Gestión de Cobranza */}
          <Dialog open={showNuevaGestion} onOpenChange={setShowNuevaGestion}>
            <DialogContent className='sm:max-w-[600px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <PhoneCall className='w-5 h-5 text-amber-600' />
                  Registrar Nueva Gestión de Cobranza
                </DialogTitle>
                <DialogDescription>
                  Registre una acción de cobranza realizada a un cliente
                </DialogDescription>
              </DialogHeader>
              
              <div className='space-y-4 py-4'>
                {/* Cliente */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Cliente *</Label>
                  <Select 
                    value={nuevaGestion.cliente_id} 
                    onValueChange={(value) => {
                      const cliente = clientesConFacturas.find(c => c.id === value);
                      setNuevaGestion(prev => ({
                        ...prev,
                        cliente_id: value,
                        cliente_nombre: cliente?.name || ''
                      }));
                    }}
                  >
                    <SelectTrigger data-testid='select-cliente-gestion'>
                      <SelectValue placeholder='Seleccione un cliente' />
                    </SelectTrigger>
                    <SelectContent>
                      {clientesConFacturas.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.name} - Deuda: ${new Intl.NumberFormat('es-CL').format(cliente.deuda)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  {/* Tipo de gestión */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Tipo de gestión *</Label>
                    <Select 
                      value={nuevaGestion.tipo_gestion} 
                      onValueChange={(value) => setNuevaGestion(prev => ({ ...prev, tipo_gestion: value }))}
                    >
                      <SelectTrigger data-testid='select-tipo-gestion'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposGestion.map(tipo => (
                          <SelectItem key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Canal */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Canal *</Label>
                    <Select 
                      value={nuevaGestion.canal} 
                      onValueChange={(value) => setNuevaGestion(prev => ({ ...prev, canal: value }))}
                    >
                      <SelectTrigger data-testid='select-canal-gestion'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {canalesGestion.map(canal => (
                          <SelectItem key={canal.value} value={canal.value}>
                            {canal.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Resumen */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Resumen de la gestión *</Label>
                  <DebouncedTextarea
                    placeholder='Describa brevemente la gestión realizada...'
                    value={nuevaGestion.resumen}
                    onChange={(e) => setNuevaGestion(prev => ({ ...prev, resumen: e.target.value }))}
                    rows={3}
                    data-testid='textarea-resumen-gestion'
                  />
                </div>

                {/* Resultado */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Resultado de la gestión</Label>
                  <Select 
                    value={nuevaGestion.resultado} 
                    onValueChange={(value) => setNuevaGestion(prev => ({ ...prev, resultado: value }))}
                  >
                    <SelectTrigger data-testid='select-resultado-gestion'>
                      <SelectValue placeholder='Seleccione el resultado' />
                    </SelectTrigger>
                    <SelectContent>
                      {resultadosGestion.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  {/* Próxima acción */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Próxima acción</Label>
                    <DebouncedInput
                      placeholder='Ej: Llamar nuevamente, enviar correo...'
                      value={nuevaGestion.proxima_accion}
                      onChange={(e) => setNuevaGestion(prev => ({ ...prev, proxima_accion: e.target.value }))}
                      data-testid='input-proxima-accion'
                    />
                  </div>

                  {/* Fecha próxima gestión */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Fecha próxima gestión</Label>
                    <Input
                      type='date'
                      value={nuevaGestion.fecha_proxima_gestion}
                      onChange={(e) => setNuevaGestion(prev => ({ ...prev, fecha_proxima_gestion: e.target.value }))}
                      data-testid='input-fecha-proxima'
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowNuevaGestion(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCrearGestion}
                  className='bg-amber-600 hover:bg-amber-700'
                  data-testid='btn-guardar-gestion'
                >
                  <Save className='w-4 h-4 mr-2' />
                  Registrar Gestión
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Nuevo Compromiso de Pago */}
          <Dialog open={showNuevoCompromiso} onOpenChange={setShowNuevoCompromiso}>
            <DialogContent className='sm:max-w-[500px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <CreditCard className='w-5 h-5 text-green-600' />
                  Registrar Compromiso de Pago
                </DialogTitle>
                <DialogDescription>
                  Registre un compromiso de pago acordado con el cliente
                </DialogDescription>
              </DialogHeader>
              
              <div className='space-y-4 py-4'>
                {/* Cliente */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Cliente *</Label>
                  <Select 
                    value={nuevoCompromiso.cliente_id} 
                    onValueChange={(value) => {
                      const cliente = clientesConFacturas.find(c => c.id === value);
                      setNuevoCompromiso(prev => ({
                        ...prev,
                        cliente_id: value,
                        cliente_nombre: cliente?.name || ''
                      }));
                    }}
                  >
                    <SelectTrigger data-testid='select-cliente-compromiso'>
                      <SelectValue placeholder='Seleccione un cliente' />
                    </SelectTrigger>
                    <SelectContent>
                      {clientesConFacturas.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.name} - Deuda: ${new Intl.NumberFormat('es-CL').format(cliente.deuda)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  {/* Monto comprometido */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Monto comprometido (CLP) *</Label>
                    <DebouncedInput
                      type='number'
                      placeholder='0'
                      value={nuevoCompromiso.monto_comprometido}
                      onChange={(e) => setNuevoCompromiso(prev => ({ ...prev, monto_comprometido: e.target.value }))}
                      data-testid='input-monto-compromiso'
                    />
                  </div>

                  {/* Fecha de compromiso */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Fecha prometida *</Label>
                    <Input
                      type='date'
                      value={nuevoCompromiso.fecha_compromiso}
                      onChange={(e) => setNuevoCompromiso(prev => ({ ...prev, fecha_compromiso: e.target.value }))}
                      data-testid='input-fecha-compromiso'
                    />
                  </div>
                </div>

                {/* Observaciones */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Observaciones</Label>
                  <DebouncedTextarea
                    placeholder='Notas adicionales sobre el compromiso...'
                    value={nuevoCompromiso.observaciones}
                    onChange={(e) => setNuevoCompromiso(prev => ({ ...prev, observaciones: e.target.value }))}
                    rows={3}
                    data-testid='textarea-observaciones-compromiso'
                  />
                </div>
              </div>

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowNuevoCompromiso(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCrearCompromiso}
                  className='bg-green-600 hover:bg-green-700'
                  data-testid='btn-guardar-compromiso'
                >
                  <Save className='w-4 h-4 mr-2' />
                  Registrar Compromiso
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Historial de Gestiones de un Cliente */}
          <Dialog open={showHistorialGestiones} onOpenChange={setShowHistorialGestiones}>
            <DialogContent className='sm:max-w-[800px] max-h-[80vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <History className='w-5 h-5 text-blue-600' />
                  Historial de Gestiones
                </DialogTitle>
                {historialGestionesData && (
                  <DialogDescription>
                    Cliente: {historialGestionesData.cliente_id}
                  </DialogDescription>
                )}
              </DialogHeader>
              
              {historialGestionesData ? (
                <div className='space-y-4 py-4'>
                  {/* Estadísticas del cliente */}
                  <Card className='p-4 bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200'>
                    <div className='grid grid-cols-4 gap-4'>
                      <div className='text-center'>
                        <p className='text-2xl font-bold text-blue-600'>{historialGestionesData.estadisticas?.total_gestiones || 0}</p>
                        <p className='text-xs text-slate-500'>Total gestiones</p>
                      </div>
                      <div className='text-center'>
                        <p className='text-2xl font-bold text-amber-600'>
                          {historialGestionesData.estadisticas?.dias_sin_gestion ?? 'N/A'}
                        </p>
                        <p className='text-xs text-slate-500'>Días sin gestión</p>
                      </div>
                      <div className='text-center'>
                        <p className='text-2xl font-bold text-green-600'>
                          {historialGestionesData.estadisticas?.compromisos_pendientes || 0}
                        </p>
                        <p className='text-xs text-slate-500'>Compromisos pend.</p>
                      </div>
                      <div className='text-center'>
                        <p className='text-2xl font-bold text-red-600'>
                          {historialGestionesData.estadisticas?.compromisos_incumplidos || 0}
                        </p>
                        <p className='text-xs text-slate-500'>Incumplidos</p>
                      </div>
                    </div>
                  </Card>

                  {/* Compromisos del cliente */}
                  {historialGestionesData.compromisos?.length > 0 && (
                    <div>
                      <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                        <CreditCard className='w-4 h-4 text-green-600' />
                        Compromisos de Pago
                      </h4>
                      <div className='space-y-2'>
                        {historialGestionesData.compromisos.map(comp => (
                          <Card key={comp.id} className={`p-3 ${comp.estado === 'incumplido' ? 'border-red-200 bg-red-50' : comp.estado === 'cumplido' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                            <div className='flex justify-between items-center'>
                              <div>
                                <p className='text-sm font-medium'>
                                  ${new Intl.NumberFormat('es-CL').format(comp.monto_comprometido)} - Fecha: {comp.fecha_compromiso}
                                </p>
                                {comp.observaciones && <p className='text-xs text-slate-500'>{comp.observaciones}</p>}
                              </div>
                              <Badge className={
                                comp.estado === 'incumplido' ? 'bg-red-100 text-red-700' : 
                                comp.estado === 'cumplido' ? 'bg-green-100 text-green-700' : 
                                'bg-amber-100 text-amber-700'
                              }>
                                {comp.estado}
                              </Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline de gestiones */}
                  <div>
                    <h4 className='text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2'>
                      <PhoneCall className='w-4 h-4 text-amber-600' />
                      Historial de Gestiones ({historialGestionesData.gestiones?.length || 0})
                    </h4>
                    <div className='space-y-2 max-h-[300px] overflow-y-auto'>
                      {historialGestionesData.gestiones?.map((gestion, idx) => {
                        const tipoInfo = tiposGestion.find(t => t.value === gestion.tipo_gestion);
                        const resultadoInfo = resultadosGestion.find(r => r.value === gestion.resultado);
                        const TipoIcon = tipoInfo?.icon || FileText;
                        
                        return (
                          <Card key={gestion.id || idx} className='p-3 border-l-4 border-l-amber-400'>
                            <div className='flex justify-between items-start'>
                              <div className='flex-1'>
                                <div className='flex items-center gap-2 mb-1'>
                                  <TipoIcon className='w-4 h-4 text-amber-600' />
                                  <span className='text-sm font-medium text-slate-800'>
                                    {tipoInfo?.label || gestion.tipo_gestion}
                                  </span>
                                  <span className='text-xs text-slate-400'>
                                    vía {canalesGestion.find(c => c.value === gestion.canal)?.label || gestion.canal}
                                  </span>
                                </div>
                                <p className='text-sm text-slate-600'>{gestion.resumen}</p>
                                {gestion.proxima_accion && (
                                  <p className='text-xs text-blue-600 mt-1'>
                                    Próxima acción: {gestion.proxima_accion} {gestion.fecha_proxima_gestion && `(${gestion.fecha_proxima_gestion})`}
                                  </p>
                                )}
                              </div>
                              <div className='text-right'>
                                <p className='text-xs text-slate-500'>
                                  {new Date(gestion.fecha).toLocaleDateString('es-CL')}
                                </p>
                                <p className='text-xs text-slate-400'>{gestion.created_by_name}</p>
                                {gestion.resultado && (
                                  <Badge className='mt-1 text-[10px]' variant='outline'>
                                    {resultadoInfo?.label || gestion.resultado}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='p-8 text-center'>
                  <RefreshCw className='w-8 h-8 animate-spin text-blue-500 mx-auto mb-2' />
                  <p className='text-sm text-slate-500'>Cargando historial...</p>
                </div>
              )}

              <DialogFooter>
                <Button 
                  variant='outline' 
                  onClick={() => {
                    if (historialGestionesData?.cliente_id) {
                      const cliente = clientesConFacturas.find(c => c.id === historialGestionesData.cliente_id);
                      setNuevaGestion(prev => ({
                        ...prev,
                        cliente_id: historialGestionesData.cliente_id,
                        cliente_nombre: cliente?.name || ''
                      }));
                      setShowHistorialGestiones(false);
                      setShowNuevaGestion(true);
                    }
                  }}
                  className='border-amber-300 text-amber-700 hover:bg-amber-50'
                >
                  <Plus className='w-4 h-4 mr-1' />
                  Nueva Gestión
                </Button>
                <Button variant='outline' onClick={() => setShowHistorialGestiones(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Crear Gestión desde Alerta */}
          <Dialog open={showCrearGestionDesdeAlerta} onOpenChange={setShowCrearGestionDesdeAlerta}>
            <DialogContent className='sm:max-w-[500px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <PhoneCall className='w-5 h-5 text-amber-600' />
                  Gestionar Alerta
                </DialogTitle>
                <DialogDescription>
                  Registre una acción de cobranza para esta alerta
                </DialogDescription>
              </DialogHeader>
              
              {alertaSeleccionada && (
                <div className='space-y-4 py-4'>
                  {/* Info de la alerta */}
                  <Card className='p-3 bg-red-50 border-red-200'>
                    <p className='text-xs text-red-600 font-medium mb-1'>Alerta seleccionada:</p>
                    <p className='text-sm text-slate-800'>{alertaSeleccionada.mensaje}</p>
                    {alertaSeleccionada.sugerencia_accion && (
                      <p className='text-xs text-blue-600 mt-2 flex items-center gap-1'>
                        <Lightbulb className='w-3 h-3' />
                        Sugerencia: {sugerenciasAccion[alertaSeleccionada.sugerencia_accion]?.descripcion}
                      </p>
                    )}
                  </Card>

                  {/* Tipo de gestión rápida */}
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium'>Acción realizada</Label>
                    <div className='grid grid-cols-2 gap-2'>
                      <Button 
                        variant='outline' 
                        className='h-auto py-3 flex flex-col items-center gap-1'
                        onClick={() => handleCrearGestionDesdeAlerta(alertaSeleccionada, 'llamada', 'telefono', 'Llamada de seguimiento realizada')}
                      >
                        <PhoneCall className='w-5 h-5 text-amber-600' />
                        <span className='text-xs'>Llamada</span>
                      </Button>
                      <Button 
                        variant='outline' 
                        className='h-auto py-3 flex flex-col items-center gap-1'
                        onClick={() => handleCrearGestionDesdeAlerta(alertaSeleccionada, 'correo_enviado', 'email', 'Correo de recordatorio enviado')}
                      >
                        <Mail className='w-5 h-5 text-blue-600' />
                        <span className='text-xs'>Correo</span>
                      </Button>
                      <Button 
                        variant='outline' 
                        className='h-auto py-3 flex flex-col items-center gap-1'
                        onClick={() => handleCrearGestionDesdeAlerta(alertaSeleccionada, 'whatsapp', 'whatsapp', 'Mensaje de WhatsApp enviado')}
                      >
                        <Phone className='w-5 h-5 text-green-600' />
                        <span className='text-xs'>WhatsApp</span>
                      </Button>
                      <Button 
                        variant='outline' 
                        className='h-auto py-3 flex flex-col items-center gap-1'
                        onClick={() => handleCrearGestionDesdeAlerta(alertaSeleccionada, 'seguimiento_interno', 'interno', 'Revisión interna realizada')}
                      >
                        <FileText className='w-5 h-5 text-slate-600' />
                        <span className='text-xs'>Nota interna</span>
                      </Button>
                    </div>
                  </div>

                  {/* Opción para solo marcar revisada */}
                  <div className='flex items-center justify-between pt-2 border-t'>
                    <p className='text-xs text-slate-500'>¿Solo marcar como revisada sin registrar gestión?</p>
                    <Button 
                      size='sm' 
                      variant='ghost'
                      onClick={() => {
                        handleActualizarAlerta(alertaSeleccionada.id, 'revisada');
                        setShowCrearGestionDesdeAlerta(false);
                        setAlertaSeleccionada(null);
                      }}
                      className='text-xs'
                    >
                      <Eye className='w-3 h-3 mr-1' />
                      Solo revisar
                    </Button>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant='outline' onClick={() => {
                  setShowCrearGestionDesdeAlerta(false);
                  setAlertaSeleccionada(null);
                }}>
                  Cancelar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Historial de Alertas */}
          <Dialog open={showHistorialAlertas} onOpenChange={setShowHistorialAlertas}>
            <DialogContent className='sm:max-w-[800px] max-h-[80vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <History className='w-5 h-5 text-slate-600' />
                  Historial de Alertas de Cobranza
                </DialogTitle>
                <DialogDescription>
                  Registro completo de alertas generadas por el sistema
                </DialogDescription>
              </DialogHeader>
              
              <div className='space-y-4 py-4'>
                {historialAlertas.length === 0 ? (
                  <div className='p-8 text-center'>
                    <AlertTriangle className='w-10 h-10 text-slate-300 mx-auto mb-2' />
                    <p className='text-sm text-slate-500'>No hay historial de alertas</p>
                  </div>
                ) : (
                  <div className='space-y-2 max-h-[400px] overflow-y-auto'>
                    {historialAlertas.map((alerta, idx) => {
                      const prioridadColor = coloresPrioridad[alerta.prioridad] || coloresPrioridad.media;
                      const AlertIcon = iconosAlerta[alerta.tipo_alerta] || AlertTriangle;
                      const estadoColor = {
                        pendiente: 'bg-amber-100 text-amber-700',
                        revisada: 'bg-blue-100 text-blue-700',
                        gestionada: 'bg-green-100 text-green-700',
                        resuelta: 'bg-slate-100 text-slate-700'
                      };
                      
                      return (
                        <Card key={alerta.id || idx} className={`p-3 ${prioridadColor.border}`}>
                          <div className='flex justify-between items-start'>
                            <div className='flex-1'>
                              <div className='flex items-center gap-2 mb-1'>
                                <AlertIcon className={`w-4 h-4 ${prioridadColor.text}`} />
                                <span className='text-sm font-medium text-slate-800'>{alerta.cliente_nombre}</span>
                                <Badge className={estadoColor[alerta.estado] || estadoColor.pendiente}>
                                  {alerta.estado}
                                </Badge>
                              </div>
                              <p className='text-xs text-slate-600'>{alerta.mensaje}</p>
                              {alerta.revisada_por_name && (
                                <p className='text-[10px] text-slate-400 mt-1'>
                                  Revisada por: {alerta.revisada_por_name}
                                </p>
                              )}
                            </div>
                            <div className='text-right'>
                              <p className='text-xs text-slate-500'>
                                {new Date(alerta.created_at).toLocaleDateString('es-CL')}
                              </p>
                              <Badge className={`text-[9px] ${prioridadColor.bg} ${prioridadColor.text}`}>
                                {alerta.prioridad}
                              </Badge>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant='outline' onClick={() => setShowHistorialAlertas(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Enviar Estado de Cuenta */}
          <Dialog open={showEnviarEstadoCuenta} onOpenChange={setShowEnviarEstadoCuenta}>
            <DialogContent className='sm:max-w-[500px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Mail className='w-5 h-5 text-blue-600' />
                  Enviar Estado de Cuenta
                </DialogTitle>
                <DialogDescription>
                  Envíe el estado de cuenta actualizado por email a un cliente
                </DialogDescription>
              </DialogHeader>
              
              <div className='space-y-4 py-4'>
                {/* Selector de cliente */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Seleccione el cliente *</Label>
                  <Select 
                    value={clienteEnvioSeleccionado?.id || ''} 
                    onValueChange={(value) => {
                      const cliente = clientesConFacturas.find(c => c.id === value);
                      setClienteEnvioSeleccionado(cliente);
                    }}
                  >
                    <SelectTrigger data-testid='select-cliente-estado-cuenta'>
                      <SelectValue placeholder='Seleccione un cliente' />
                    </SelectTrigger>
                    <SelectContent>
                      {clientesConFacturas.map(cliente => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.name} - Deuda: ${new Intl.NumberFormat('es-CL').format(cliente.deuda)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {clienteEnvioSeleccionado && (
                  <Card className='p-3 bg-blue-50 border-blue-200'>
                    <p className='text-sm font-medium text-blue-800'>{clienteEnvioSeleccionado.name}</p>
                    <p className='text-xs text-blue-600'>
                      Deuda total: ${new Intl.NumberFormat('es-CL').format(clienteEnvioSeleccionado.deuda)}
                    </p>
                  </Card>
                )}

                {!comunicacionesPanel?.configuracion?.email_configurado && (
                  <Card className='p-3 bg-amber-50 border-amber-200'>
                    <div className='flex items-center gap-2'>
                      <AlertTriangle className='w-4 h-4 text-amber-600' />
                      <p className='text-xs text-amber-700'>
                        Modo simulado: El envío quedará registrado pero no se enviará realmente
                      </p>
                    </div>
                  </Card>
                )}
              </div>

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowEnviarEstadoCuenta(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={() => clienteEnvioSeleccionado && handleEnviarEstadoCuenta(clienteEnvioSeleccionado.id)}
                  disabled={!clienteEnvioSeleccionado || enviandoComunicacion}
                  className='bg-blue-600 hover:bg-blue-700'
                  data-testid='btn-confirmar-envio-estado'
                >
                  {enviandoComunicacion ? (
                    <RefreshCw className='w-4 h-4 mr-2 animate-spin' />
                  ) : (
                    <Mail className='w-4 h-4 mr-2' />
                  )}
                  Enviar Estado de Cuenta
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Enviar Recordatorio */}
          <Dialog open={showEnviarRecordatorio} onOpenChange={setShowEnviarRecordatorio}>
            <DialogContent className='sm:max-w-[500px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <AlertTriangle className='w-5 h-5 text-amber-600' />
                  Enviar Recordatorio de Pago
                </DialogTitle>
                <DialogDescription>
                  Envíe un recordatorio por una factura específica
                </DialogDescription>
              </DialogHeader>
              
              <div className='space-y-4 py-4'>
                {/* Selector de factura */}
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Seleccione la factura *</Label>
                  <Select 
                    value={facturaEnvioSeleccionada?.id || ''} 
                    onValueChange={(value) => {
                      const factura = cobranzasData.facturas?.find(f => f.id === value);
                      setFacturaEnvioSeleccionada(factura);
                    }}
                  >
                    <SelectTrigger data-testid='select-factura-recordatorio'>
                      <SelectValue placeholder='Seleccione una factura' />
                    </SelectTrigger>
                    <SelectContent>
                      {cobranzasData.facturas?.filter(f => f.pending_amount_clp > 0).map(factura => (
                        <SelectItem key={factura.id} value={factura.id}>
                          {factura.folio || factura.numero_factura} - {factura.cliente?.company || factura.cliente?.name} - ${new Intl.NumberFormat('es-CL').format(factura.pending_amount_clp)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {facturaEnvioSeleccionada && (
                  <Card className='p-3 bg-amber-50 border-amber-200'>
                    <p className='text-sm font-medium text-amber-800'>
                      Factura: {facturaEnvioSeleccionada.folio || facturaEnvioSeleccionada.numero_factura}
                    </p>
                    <p className='text-xs text-amber-600'>
                      Cliente: {facturaEnvioSeleccionada.cliente?.company || facturaEnvioSeleccionada.cliente?.name}
                    </p>
                    <p className='text-xs text-amber-600'>
                      Monto pendiente: ${new Intl.NumberFormat('es-CL').format(facturaEnvioSeleccionada.pending_amount_clp)}
                    </p>
                    {facturaEnvioSeleccionada.fecha_vencimiento && (
                      <p className='text-xs text-amber-600'>
                        Vencimiento: {facturaEnvioSeleccionada.fecha_vencimiento}
                      </p>
                    )}
                  </Card>
                )}
              </div>

              <DialogFooter className='gap-2'>
                <Button variant='outline' onClick={() => setShowEnviarRecordatorio(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={() => facturaEnvioSeleccionada && handleEnviarRecordatorio(facturaEnvioSeleccionada.id)}
                  disabled={!facturaEnvioSeleccionada || enviandoComunicacion}
                  className='bg-amber-600 hover:bg-amber-700'
                  data-testid='btn-confirmar-envio-recordatorio'
                >
                  {enviandoComunicacion ? (
                    <RefreshCw className='w-4 h-4 mr-2 animate-spin' />
                  ) : (
                    <Mail className='w-4 h-4 mr-2' />
                  )}
                  Enviar Recordatorio
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal: Configuración de Comunicaciones */}
          <Dialog open={showConfigComunicaciones} onOpenChange={setShowConfigComunicaciones}>
            <DialogContent className='sm:max-w-[600px]'>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <Settings className='w-5 h-5 text-slate-600' />
                  Configuración de Comunicaciones
                </DialogTitle>
                <DialogDescription>
                  Configure las reglas de envío automático de comunicaciones
                </DialogDescription>
              </DialogHeader>
              
              {configComunicaciones ? (
                <div className='space-y-4 py-4'>
                  {/* Estado general */}
                  <Card className={`p-4 ${configComunicaciones.email_configurado ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-3'>
                        {configComunicaciones.email_configurado ? (
                          <CheckCircle className='w-5 h-5 text-green-600' />
                        ) : (
                          <AlertTriangle className='w-5 h-5 text-amber-600' />
                        )}
                        <div>
                          <p className='text-sm font-medium'>
                            {configComunicaciones.email_configurado ? 'Resend Configurado' : 'Resend No Configurado'}
                          </p>
                          <p className='text-xs text-slate-500'>
                            {configComunicaciones.email_configurado 
                              ? 'Los correos se envían normalmente' 
                              : 'Los correos se registran pero no se envían'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <div className='grid grid-cols-2 gap-4'>
                    {/* Comunicaciones activas */}
                    <div className='space-y-2'>
                      <Label className='text-sm'>Comunicaciones automáticas</Label>
                      <Badge className={configComunicaciones.comunicaciones_activas ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {configComunicaciones.comunicaciones_activas ? 'Activas' : 'Desactivadas'}
                      </Badge>
                    </div>

                    {/* Estados de cuenta */}
                    <div className='space-y-2'>
                      <Label className='text-sm'>Estados de cuenta mensuales</Label>
                      <Badge className={configComunicaciones.enviar_estado_cuenta_mensual ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                        {configComunicaciones.enviar_estado_cuenta_mensual ? 'Activado' : 'Desactivado'}
                      </Badge>
                    </div>

                    {/* Recordatorios vencidas */}
                    <div className='space-y-2'>
                      <Label className='text-sm'>Recordatorios facturas vencidas</Label>
                      <Badge className={configComunicaciones.enviar_recordatorios_vencidas ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                        {configComunicaciones.enviar_recordatorios_vencidas ? 'Activado' : 'Desactivado'}
                      </Badge>
                    </div>

                    {/* Recordatorios compromisos */}
                    <div className='space-y-2'>
                      <Label className='text-sm'>Recordatorios compromisos</Label>
                      <Badge className={configComunicaciones.enviar_recordatorios_compromisos ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                        {configComunicaciones.enviar_recordatorios_compromisos ? 'Activado' : 'Desactivado'}
                      </Badge>
                    </div>
                  </div>

                  {/* Info del remitente */}
                  <Card className='p-3 bg-slate-50'>
                    <p className='text-xs text-slate-500'>Email remitente</p>
                    <p className='text-sm font-medium'>{configComunicaciones.email_remitente}</p>
                    <p className='text-xs text-slate-500 mt-2'>Nombre remitente</p>
                    <p className='text-sm font-medium'>{configComunicaciones.nombre_remitente}</p>
                  </Card>

                  {/* Días de recordatorio */}
                  <Card className='p-3 bg-slate-50'>
                    <p className='text-xs text-slate-500 mb-2'>Días de recordatorio para facturas vencidas</p>
                    <div className='flex gap-2'>
                      {configComunicaciones.dias_recordatorio_vencida?.map(dia => (
                        <Badge key={dia} variant='outline' className='text-xs'>
                          Día {dia}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                </div>
              ) : (
                <div className='p-8 text-center'>
                  <RefreshCw className='w-6 h-6 animate-spin text-slate-400 mx-auto mb-2' />
                  <p className='text-sm text-slate-500'>Cargando configuración...</p>
                </div>
              )}

              <DialogFooter>
                <Button variant='outline' onClick={() => setShowConfigComunicaciones(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
};

export default Finanzas;
